import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  Snackbar,
  CircularProgress,
  Checkbox,
  Autocomplete,
  MenuList,
  ClickAwayListener,
  Popper,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import TestingLaboratoryService from "../../../services/testinglaboratoryService";
import * as XLSX from "xlsx";
import { decryptText, decryptAppId } from '../../../utils/encryption';
import { parseEncryptedQuery } from '../../../utils/encryption';
import {
  insertDANC,
  updateDANCBySourceId,
  getDANC
} from '../../../services/apiCalibration';
import { getScrutinyRemarks, getClauseNo } from '../../../services/api';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import LaboratoryService from "../../../services/laboratoryService";
import axios from 'axios';

// Adequacy Check Popup Component
const AdequacyCheckPopup = React.memo(
  ({
    open,
    onClose,
    scopeId,
    scopeTitle,
    adequacyCheck,
    onAdequacyChange,
    onRemarksChange,
    onSubmit,
    loading,
    // Clause-related props for Assessment users
    clauseNo = [],
    selectedClause,
    onClauseChange,
    userTypeId = 0,
  }) => {
    const adequacy = adequacyCheck || { status: "", remarks: "" };

    // Scrutiny remarks state
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);

    // Fetch scrutiny remarks when dialog opens
    useEffect(() => {
      if (open) {
        fetchScrutinyRemarks();
      }
    }, [open]);

    const fetchScrutinyRemarks = async () => {
      setLoadingRemarks(true);
      try {
        const responseApi = await getScrutinyRemarks({
          sectionName: 'Scope',
          type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
        });
        const response = responseApi.data;
        if (response.success && response.data) {
          setScrutinyRemarks(response.data);
          console.log('Scope: Loaded scrutiny remarks:', response.data.length);
        } else {
          setScrutinyRemarks([]);
        }
      } catch (error) {
        console.error('Error fetching scrutiny remarks:', error);
        setScrutinyRemarks([]);
      } finally {
        setLoadingRemarks(false);
      }
    };

    const handleRemarksChange = (newValue) => {
      onRemarksChange(newValue);
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          📋 Adequacy Check - {scopeTitle}
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                    },
                  },
                }}
              >
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={adequacy.status}
                  onChange={(e) => onAdequacyChange(e.target.value)}
                  label="Document Status"
                >
                  <MenuItem value="">-- Select Status --</MenuItem>
                  <MenuItem value="Adequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#28a745",
                        }}
                      />
                      Adequate
                    </Box>
                  </MenuItem>
                  <MenuItem value="Inadequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#dc3545",
                        }}
                      />
                      Inadequate
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clause Number dropdown for Assessment users when status is Inadequate */}
            {adequacy.status === "Inadequate" && userTypeId === 6 && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clauseNo}
                  getOptionLabel={(option) => option?.DisplayName || option?.ClauseNo || option}
                  value={selectedClause}
                  onChange={(event, newValue) => {
                    console.log('📍 ScopeSection - Clause selected:', newValue);
                    if (onClauseChange) onClauseChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="📋 Select Clause Number"
                      variant="outlined"
                      size="medium"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option?.ClauseNo || option?.DisplayName || option}
                        </Typography>
                        {option?.DisplayName && option?.ClauseNo && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.DisplayName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  sx={{ mt: 1 }}
                />
              </Grid>
            )}

            {adequacy.status === "Inadequate" && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  loading={loadingRemarks}
                  options={scrutinyRemarks}
                  value={adequacy.remarks || ''}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.inadequate_remarks || '';
                  }}
                  onChange={(event, newValue) => {
                    let remarkText = '';
                    if (typeof newValue === 'string') {
                      // User typed text
                      remarkText = newValue;
                    } else if (newValue && newValue.inadequate_remarks) {
                      // User selected from dropdown
                      remarkText = newValue.inadequate_remarks;
                    }
                    handleRemarksChange(remarkText);
                  }}
                  onInputChange={(event, newInputValue) => {
                    // Handle typing in the input field
                    handleRemarksChange(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      multiline
                      rows={3}
                      label={`Remarks (Required)${scrutinyRemarks.length > 0 ? ` - ${scrutinyRemarks.length} suggestions` : ''}`}
                      placeholder={scrutinyRemarks.length > 0 ?
                        "Select from dropdown or type your own remarks..." :
                        "Please type your remarks here."
                      }
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {adequacy.status && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity={
                  adequacy.isSubmitted
                    ? "info"
                    : adequacy.status === "Adequate"
                      ? "success"
                      : "warning"
                }
                sx={{ borderRadius: 2 }}
              >
                {adequacy.isSubmitted
                  ? `✅ Adequacy check submitted on ${new Date(
                    adequacy.submittedAt
                  ).toLocaleString()}`
                  : adequacy.status === "Adequate"
                    ? "✓ This scope is marked as Adequate."
                    : "⚠ Please address the remarks above."}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={onClose} sx={{ color: "#6c757d" }}>
            Cancel
          </Button>
          {!adequacy.isSubmitted && adequacy.status && (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={
                loading ||
                (adequacy.status === "Inadequate" && !adequacy.remarks?.trim())
              }
              sx={{
                background:
                  adequacy.status === "Adequate"
                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                    : "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : (
                "📤 Submit Adequacy Check"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

const MedicalScopeSection = ({ data, onUpdate, fields = [], sectionType }) => {
  const [searchParams] = useSearchParams();
  const { orgId: urlOrgId } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const [commonFacilityType, setCommonFacilityType] = useState([]);
  const location = useLocation();

  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log('📍 ScopeSection - Decrypted orgId from URL query:', decryptedOrgId);
        console.log('📍 ScopeSection - Original encrypted orgId:', encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        console.log('⚠️ ScopeSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  const userTypeId = localStorage.getItem('userTypeId');
  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        console.log('📍 ScopeSection - Decrypted appId from URL query:', decryptedAppId);
        console.log('📍 ScopeSection - Original encrypted appId:', encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        console.log('⚠️ ScopeSection - Failed to decrypt appId, using original:', encryptedAppId, error);
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get orgId with priority: isSubUser check > URL query (encrypted) > URL params > null
  const getOrgId = useCallback(() => {


    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      console.log('📍 ScopeSection - Using decrypted orgId from URL query:', queryOrgId);
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      console.log('📍 ScopeSection - Using orgId from URL params:', urlOrgId);
      return urlOrgId;
    }

    // No fallback - orgId must be provided via URL
    console.log('⚠️ ScopeSection - No orgId found in URL parameters');
    return null;
  }, [getOrgIdFromQuery, urlOrgId]);

  // Function to get appId with priority: isSubUser check > URL query (encrypted) only
  const getAppId = useCallback(() => {


    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      console.log('📍 ScopeSection - Using decrypted appId from URL query:', queryAppId);
      return queryAppId;
    }

    // No fallback - appId must be provided via URL query parameters
    console.log('⚠️ ScopeSection - No appId found in URL query parameters');
    return null;
  }, [getAppIdFromQuery]);

  // Assessment parameter extraction functions for Assessment users
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return 0;
  };

  // Function to get labType from URL query parameters with decryption
  const getLabTypeFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedLabType = searchParams.get("labType");
    if (encryptedLabType) {
      try {
        const decryptedLabType = decryptText(encryptedLabType);
        console.log(
          "📍 Using decrypted labType from URL query:",
          decryptedLabType
        );
        return decryptedLabType;
      } catch (error) {
        return encryptedLabType;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return 0;
  };

  const getCapacityFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedCapacity = searchParams.get("capacity");
    if (encryptedCapacity) {
      try {
        const decryptedCapacity = decryptText(encryptedCapacity);
        console.log(
          "📍 Using decrypted capacity from URL query:",
          decryptedCapacity
        );
        return decryptedCapacity;
      } catch (error) {
        return encryptedCapacity;
      }
    }
    return null;
  };

  const getUserTypeId = useCallback(() => {
    const userType = parseInt(localStorage.getItem('userTypeId') || '0');
    console.log('📍 ScopeSection - User Type ID:', userType);
    return userType;
  }, []);
  const [activeTab, setActiveTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  // const [editingScope, setEditingScope] = useState(null); // Removed - not used in new implementation
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [uploadFile, setUploadFile] = useState(null);

  // Scope Master Data State
  const [scopeMasterData, setScopeMasterData] = useState([]);
  const [scopeMasterLoading, setScopeMasterLoading] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [selectedScopeData, setSelectedScopeData] = useState([]); // Store complete data for selected scopes across all pages
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(50);

  // Filter states for existing scopes
  const [ptAvailabilityFilter, setPtAvailabilityFilter] = useState("");
  const [qualitativeQuantitativeFilter, setQualitativeQuantitativeFilter] =
    useState("");

  // Clause number states for Assessment users
  const [clauseNo, setClauseNo] = useState([]);
  const [selectedClause, setSelectedClause] = useState(null);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editingScopeId, setEditingScopeId] = useState(null);
  const [editReadOnlyData, setEditReadOnlyData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Snackbar state (reusing notification state)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Multiple delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // 'single' or 'multiple'
  const [singleDeleteId, setSingleDeleteId] = useState(null);

  // Combined selection state for both delete and bind operations on existing scopes
  const [selectedExistingScopes, setSelectedExistingScopes] = useState([]);
  const [bindingModalOpen, setBindingModalOpen] = useState(false);
  const [bindingData, setBindingData] = useState({
    ptProgrammes: [],
    equipment: [],
    referenceMaterials: [],
  });
  const [bindingSelections, setBindingSelections] = useState({
    selectedPT: [],
    selectedEquipment: [],
    selectedReferenceMaterial: [],
  });
  const [bindingLoading, setBindingLoading] = useState(false);

  // Search states for binding items
  const [searchFilters, setSearchFilters] = useState({
    ptSearch: '',
    equipmentSearch: '',
    referenceMaterialSearch: ''
  });

  // Adequacy Check States
  const [applicationStage, setApplicationStage] = useState(null);
  const [adequacyChecks, setAdequacyChecks] = useState({});
  const [adequacyModalOpen, setAdequacyModalOpen] = useState(false);
  const [currentAdequacyScopeId, setCurrentAdequacyScopeId] = useState(null);
  const [currentAdequacyScopeTitle, setCurrentAdequacyScopeTitle] =
    useState("");
  const [adequacyLoading, setAdequacyLoading] = useState(false);

  // DA-NC Integration States
  const [dancRecords, setDancRecords] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Current user for DA NC system - get from auth context or session
  const [currentUser] = useState(() => {
    return (
      localStorage.getItem("userName") ||
      localStorage.getItem("currentUser") ||
      "System User"
    );
  });

  // Current user ID for DA NC system - get numeric ID for Insertedby field
  const [currentUserId] = useState(() => {
    return (
      parseInt(localStorage.getItem("userId")) ||
      parseInt(localStorage.getItem("currentUserId")) ||
      0
    );
  });

  // Bulk Adequacy Check States
  const [bulkAdequacyModalOpen, setBulkAdequacyModalOpen] = useState(false);
  const [selectedAdequacyScopes, setSelectedAdequacyScopes] = useState([]);
  const [bulkAdequacyStatus, setBulkAdequacyStatus] = useState("");
  const [bulkAdequacyRemarks, setBulkAdequacyRemarks] = useState("");
  const [bulkAdequacyLoading, setBulkAdequacyLoading] = useState(false);

  // Bulk Adequacy Scrutiny Remarks States
  const [bulkScrutinyRemarks, setBulkScrutinyRemarks] = useState([]);
  const [bulkLoadingRemarks, setBulkLoadingRemarks] = useState(false);

  // Checkbox dropdown states for bulk adequacy
  const [selectedBulkRemarksOptions, setSelectedBulkRemarksOptions] = useState([]);
  const [isBulkCheckboxDropdownOpen, setIsBulkCheckboxDropdownOpen] = useState(false);

  // Unfreeze functionality states
  const [unfreezeRecords, setUnfreezeRecords] = useState([]);

  // Test Witness functionality states
  const [selectedTestWitnessScopes, setSelectedTestWitnessScopes] = useState([]);
  const [testWitnessConfirmOpen, setTestWitnessConfirmOpen] = useState(false);
  const [testWitnessLoading, setTestWitnessLoading] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);

  const [selectedSubGroupMaster, setSelectedSubGroupMaster] = useState('');

  const [subGroups, setSubGroups] = useState([]);
  const [subGroupsLoading, setSubGroupsLoading] = useState(false);
  const [selectedSubGroup, setSelectedSubGroup] = useState('');

  const [formData, setFormData] = useState({
    disciplineId: "",
    disciplineName: "",
    groupId: "",
    groupName: "",
    appId: "",
    orgId: "",
    ...data,
  });

  // Instructions state
  const [instructions, setInstructions] = useState([]);
  const [instructionsLoading, setInstructionsLoading] = useState(false);

  // Sample data for existing scopes - will be replaced with API data
  const [existingScopes, setExistingScopes] = useState([]);
  const [existingScopesLoading, setExistingScopesLoading] = useState(false);

  // Pagination states for existing scopes
  const [existingScopesPage, setExistingScopesPage] = useState(1);
  const [existingScopesPageSize, setExistingScopesPageSize] = useState(50);
  const [existingScopesTotalPages, setExistingScopesTotalPages] = useState(1);
  const [existingScopesTotalRecords, setExistingScopesTotalRecords] =
    useState(0);
  const [existingScopesSearchQuery, setExistingScopesSearchQuery] =
    useState("");

  // New scope form data - removed in favor of direct master data selection
  /*
  const [newScope, setNewScope] = useState({
    testCategory: '',
    testName: '',
    sampleType: '',
    method: '',
    range: '',
    turnaroundTime: '',
    qualityControl: '',
    accreditationStandard: ''
  });
  */

  // Add Other Scope Modal State
  const [addOtherModalOpen, setAddOtherModalOpen] = useState(false);
  const [otherScopeForm, setOtherScopeForm] = useState({
    groupTested: "",
    typeOfTest: "",
    scopeStandard: "",
    rangeType: "Quantitative", // 'Quantitative', 'Qualitative', 'Calculative', or 'Others'
    rangeLL: "",
    rangeLLUnit: "",
    rangeUL: "",
    rangeULUnit: "",
    qualitativeRemarks: "", // New field for qualitative type
    mu: "",
    cv: "",
    testPerformedAt: "PermanentSiteFacility", // 'PermanentSiteFacility', 'TemporarySiteFacility', 'Laboratory'
  });
  const [otherScopeLoading, setOtherScopeLoading] = useState(false);

  // Data preview states
  const [previewData, setPreviewData] = useState([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false); // Used in handleFileUpload

  // Bulk Update Preview State
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewFileName, setPreviewFileName] = useState("");

  // Preview Edit State
  const [editedPreviewData, setEditedPreviewData] = useState([]);
  const [originalPreviewData, setOriginalPreviewData] = useState([]); // Store original Excel data
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Performance Score Dialog States
  const [performanceScoreDialogOpen, setPerformanceScoreDialogOpen] = useState(false);
  const [selectedPtForScore, setSelectedPtForScore] = useState(null);
  const [performanceScoreForm, setPerformanceScoreForm] = useState({
    labCode: "",
    performanceScore: "",
  });
  const [performanceScoreLoading, setPerformanceScoreLoading] = useState(false);
  const [existingPerformanceScores, setExistingPerformanceScores] = useState([]);
  const [ptScoresMap, setPtScoresMap] = useState({}); // Map of scopeId_ptId -> hasData

  // Notification functions
  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const closeNotification = () => {
    setNotification({ open: false, message: "", severity: "success" });
  };

  // Load DA-NC data function
  const loadDANCData = useCallback(async () => {
    if (!formData.orgId || !formData.appId) return;

    try {
      // Determine expected NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');
      let expectedNCType = 'Document Adequacy'; // Default

      if (userTypeId === '4') {
        expectedNCType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        expectedNCType = 'Assessment';
      }

      console.log(`🔍 ScopeSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);

      // Get all DA NC records for this org and app
      const response = await getDANC({
        orgid: formData.orgId,
        appid: formData.appId,
        assessorId: assessorId,
        isactive: 1,
      });

      console.log("Scope DA-NC API Response:", response);

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        console.log(`📋 Found ${response.data.data.length} total records for Scope section`);

        // Filter Records for Scope section and by NCtype
        const scopeDANCRecords = response.data.data.filter(
          (record) =>
            (record.SectionName === "Scope" ||
              record.SectionName === "Scope Details") &&
            record.NCtype === expectedNCType
        );

        console.log(`🎯 Filtered to ${scopeDANCRecords.length} scope records with NCtype: ${expectedNCType}`);

        setDancRecords(scopeDANCRecords);

        // Create adequacy checks from DA NC data
        const dancAdequacyChecks = {};

        scopeDANCRecords.forEach((record) => {
          let sectionKey = record.sourceid; // Use sourceid as the key for scopes

          if (sectionKey) {
            dancAdequacyChecks[sectionKey] = {
              status: record.InformationStatus || "",
              remarks: record.InformationProvided || record.Observation || "",
              isSubmitted: false, // Allow users to modify and resubmit
              submittedAt: record.updatedate || record.Insertdate,
              dancRecord: record, // Store original record for reference
              clauseNo: record.ClauseNo || "", // Include clause number for display
            };

            console.log(
              `Mapped scope Record to scope ID "${sectionKey}":`,
              {
                status: record.InformationStatus,
                remarks: record.InformationProvided || record.Observation,
                sourceid: record.sourceid,
              }
            );
          }
        });

        // Merge with existing adequacy checks
        setAdequacyChecks((prev) => {
          const merged = {
            ...prev,
            ...dancAdequacyChecks,
          };
          console.log("Final merged scope adequacy checks:", merged);
          return merged;
        });

        showNotification(
          `${scopeDANCRecords.length} scope Records loaded and bound to adequacy checks`,
          "success"
        );
      } else {
        console.log("No scope Records found in response");
        setDancRecords([]);
      }
    } catch (error) {
      console.error("Error loading scope DA-NC data:", error);
      showNotification("Error loading DA-NC data: " + error.message, "error");
    }
  }, [formData.orgId, formData.appId]);

  // Unfreeze functionality: Check if section is unfrozen
  const isSectionUnfrozen = useCallback((sectionName) => {
    if (!Array.isArray(unfreezeRecords)) {
      return false;
    }

    const unfreezeRecord = unfreezeRecords.find(
      record => record.sectionname === sectionName
    );

    if (!unfreezeRecord) {
      return false;
    }

    // Check if unfreeze record is still valid (not expired)
    const expiryDate = new Date(unfreezeRecord.expirydate);
    // Reset expiry date time to start of day for date-only comparison
    expiryDate.setHours(0, 0, 0, 0);

    const currentDate = new Date();
    // Reset current date time to start of day for date-only comparison
    currentDate.setHours(0, 0, 0, 0);

    return currentDate <= expiryDate;
  }, [unfreezeRecords]);

  // Load unfreeze records
  const loadUnfreezeRecords = useCallback(async () => {
    const appId = getAppId();
    const organizationId = getOrgId();

    if (!appId || !organizationId) {
      setUnfreezeRecords([]);
      return;
    }

    try {
      const recordsApi = await UnfreezeApiService.getUnfreezeRecords(appId, organizationId);
      const records = recordsApi.data;
      // Ensure records is always an array
      if (Array.isArray(records)) {
        setUnfreezeRecords(records);
      } else {
        setUnfreezeRecords([]);
      }
    } catch (error) {
      console.error('❌ Error loading unfreeze records:', error);
      setUnfreezeRecords([]);
    }
  }, [getAppId, getOrgId]);

  // Memoized unfreeze status to prevent infinite re-renders
  const isScopeSectionUnfrozen = useMemo(() => {
    return isSectionUnfrozen('Scope');
  }, [isSectionUnfrozen]);

  // Load scope master data
  const loadScopeMasterData = useCallback(
    async (page = 1, search = "") => {
      if (!formData.disciplineId || !formData.groupId) {
        showNotification("Discipline & Group ID not found", "error");
        return;
      }

      const labTypeId = localStorage.getItem("labTypeId") || "1";

      setScopeMasterLoading(true);
      try {
        const result = await TestingLaboratoryService.getScopeMaster(
          labTypeId,
          formData.disciplineId,
          formData.groupId,
          page,
          pageSize,
          search
        );

        if (result.success) {
          setScopeMasterData(result.data);
          setCurrentPage(result.pagination.currentPage);
          setTotalPages(result.pagination.totalPages);
          setTotalRecords(result.pagination.totalRecords);

          if (result.data.length === 0 && search) {
            showNotification(
              "No scope data found for the search criteria",
              "info"
            );
          }
        } else {
          showNotification(
            result.message || "Failed to load scope data",
            "error"
          );
          setScopeMasterData([]);
        }
      } catch (error) {
        console.error("Error loading scope master data:", error);
        showNotification("Error loading scope data: " + error.message, "error");
        setScopeMasterData([]);
      } finally {
        setScopeMasterLoading(false);
      }
    },
    [formData.disciplineId, formData.groupId, pageSize]
  );

  // Extract parameters from URL and localStorage
  useEffect(() => {
    // Parse encrypted query parameters
    const { disciplineId, disciplineName, groupId, groupName, tab } = parseEncryptedQuery(searchParams);

    // Get appId and orgId from encrypted query parameters
    const currentAppId = getAppId() || "";
    const currentOrgId = getOrgId() || "";
    console.log('🔍 ScopeSection - Using orgId:', currentOrgId, 'appId:', currentAppId);

    // If tab parameter is provided, set it directly (for "View Scope" navigation)
    if (tab !== null && tab >= 0 && tab <= 3) {
      setActiveTab(tab);
      console.log('🔓 Scope Section - Setting active tab to:', tab);
    }

    console.log('🔓 Scope Section - Decrypted Parameters:', {
      disciplineId,
      disciplineName,
      groupId,
      groupName,
      tab
    });

    // Create a unique key for the current page based on discipline
    const currentPageKey = `scope_${disciplineId}_${groupId}_${currentAppId}_${currentOrgId}`;
    const lastPageKey = sessionStorage.getItem('lastScopePageKey');

    // Check if discipline/page has changed and trigger reload
    if (lastPageKey && lastPageKey !== currentPageKey && disciplineId && groupId) {
      console.log('🔄 Page parameters changed - triggering reload');
      sessionStorage.setItem('lastScopePageKey', currentPageKey);
      window.location.reload();
      return; // Exit early since page will reload
    }

    // Store the current page key for next comparison
    if (disciplineId && groupId) {
      sessionStorage.setItem('lastScopePageKey', currentPageKey);
    }

    setFormData((prev) => ({
      ...prev,
      disciplineId: disciplineId || "",
      disciplineName: disciplineName || "",
      groupId: groupId || "",
      groupName: groupName || "",
      appId: currentAppId,
      orgId: currentOrgId,
    }));
  }, [searchParams, getAppId, getOrgId]);

  // Fetch instructions from API
  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        setInstructionsLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
        const response = await fetch(`${API_BASE_URL}/instructions?sectionName=Scope&labTypeId=1`);
        const data = await response.json();

        if (data.success && data.data.instructions) {
          // Sort instructions by orderNo
          const sortedInstructions = data.data.instructions.sort((a, b) => a.orderNo - b.orderNo);
          setInstructions(sortedInstructions);
        }
      } catch (error) {
        console.error('Error fetching instructions:', error);
        // Fallback to default instructions if API fails
        setInstructions([]);
      } finally {
        setInstructionsLoading(false);
      }
    };

    fetchInstructions();
  }, []);

  // Fetch clause numbers from API
  const fetchClauseNo = useCallback(async () => {
    try {
      console.log('Fetching clause numbers for Lab Staff section...');

      const clauseResponse = await getClauseNo({ standard: '2017', feild: 'Testing' });

      if (clauseResponse.data?.success && clauseResponse.data?.data) {
        setClauseNo(clauseResponse.data.data);
        console.log('Loaded clause numbers:', clauseResponse.data.data.length);
      } else {
        console.warn('No clause numbers found or API returned success: false');
        setClauseNo([]);
      }
    } catch (error) {
      console.error('Error fetching clause numbers:', error);
      setClauseNo([]);
    }
  }, []);

  // Fetch clause numbers when component loads for Assessment users
  useEffect(() => {
    if (getUserTypeId() === 6) {
      fetchClauseNo();
    }
  }, [fetchClauseNo, getUserTypeId]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  // Load scope master data when tab 1 (Add Scope) is active and discipline is available
  useEffect(() => {
    if (
      activeTab === 1 &&
      formData.disciplineId &&
      formData.groupId &&
      scopeMasterData.length === 0
    ) {
      loadScopeMasterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, formData.disciplineId, formData.groupId, scopeMasterData.length]);

  // Load DA-NC data when form data is available
  useEffect(() => {
    if (formData.orgId && formData.appId) {
      loadDANCData();
    }
  }, [formData.orgId, formData.appId, refreshTrigger, loadDANCData]);

  // Load unfreeze records when org and app IDs are available
  useEffect(() => {
    if (getOrgId() && getAppId()) {
      loadUnfreezeRecords();
    }
  }, [loadUnfreezeRecords, getOrgId, getAppId]);

  // Check if a PT has performance score data
  const checkPtHasData = useCallback(async (scopeId, ptId) => {
    try {
      const response = await TestingLaboratoryService.getPerformanceScores({
        labTypeId: formData.labTypeId || 1,
        scopeId: scopeId,
      });

      if (response.success && response.data) {
        const ptScores = response.data.filter(
          (score) => score.ptId === ptId || score.ptId === parseInt(ptId)
        );
        return ptScores.length > 0;
      }
      return false;
    } catch (error) {
      console.error("Error checking PT score:", error);
      return false;
    }
  }, [formData.labTypeId]);

  const loadExistingScopes = useCallback(
    async (
      page = existingScopesPage,
      pageSize = existingScopesPageSize,
      search = existingScopesSearchQuery
    ) => {
      if (!formData.appId || !formData.orgId || !formData.disciplineId || !formData.groupId) {
        return;
      }

      setExistingScopesLoading(true);
      try {
        const result = await TestingLaboratoryService.getExistingScopes(
          formData.appId,
          formData.orgId,
          formData.disciplineId,
          formData.groupId,
          page,
          pageSize,
          search
        );

        if (result.success) {
          setExistingScopes(result.data);
          if (result.pagination) {
            setExistingScopesPage(result.pagination.currentPage);
            setExistingScopesPageSize(result.pagination.pageSize);
            setExistingScopesTotalPages(result.pagination.totalPages);
            setExistingScopesTotalRecords(result.pagination.totalRecords);
          }

          // Check performance scores for all PT IDs
          const scoresMap = {};
          for (const scope of result.data) {
            if (scope.Pt_id) {
              const ptIds = scope.Pt_id.split(",");
              for (const ptId of ptIds) {
                const trimmedPtId = ptId.trim();
                const key = `${scope.Id}_${trimmedPtId}`;
                scoresMap[key] = await checkPtHasData(scope.Id, trimmedPtId);
              }
            }
          }
          setPtScoresMap(scoresMap);
        } else {
          showNotification(
            result.message || "Failed to load existing scopes",
            "error"
          );
          setExistingScopes([]);
        }
      } catch (error) {
        console.error("Error loading existing scopes:", error);
        showNotification(
          "Error loading existing scopes: " + error.message,
          "error"
        );
        setExistingScopes([]);
      } finally {
        setExistingScopesLoading(false);
      }
    },
    [
      formData.appId,
      formData.orgId,
      formData.disciplineId,
      formData.groupId,
      existingScopesPage,
      existingScopesPageSize,
      existingScopesSearchQuery,
    ]
  );

  // Load existing scopes when formData is ready
  useEffect(() => {
    if (formData.appId && formData.orgId && formData.disciplineId && formData.groupId) {
      loadExistingScopes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.appId, formData.orgId, formData.disciplineId, formData.groupId]);

  // Clear selections when scopes change
  useEffect(() => {
    setSelectedExistingScopes([]);
  }, [existingScopes]);

  useEffect(() => {
  if (formData.disciplineId && formData.groupId) {
    fetchSubGroups();
  }
}, [formData.disciplineId, formData.groupId]);

  const fetchSubGroups = useCallback(async () => {
  const labTypeId = localStorage.getItem('labTypeId') || '1';
  if (!formData.disciplineId || !formData.groupId) return;

  setSubGroupsLoading(true);
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
    const response = await axios.get(`${API_BASE_URL}/admin-master/subgroups`, {
      params: {
        labTypeId,
        disciplineId: formData.disciplineId,
        groupId: formData.groupId,
      },
    });

    if (response.data.status === 'success') {
      setSubGroups(response.data.data || []);
    } else {
      setSubGroups([]);
    }
  } catch (error) {
    console.error('Error fetching sub-groups:', error);
    setSubGroups([]);
  } finally {
    setSubGroupsLoading(false);
  }
}, [formData.disciplineId, formData.groupId]);

  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`
      );
      const result = await response.json();

      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        console.log("Stage not found, defaulting to stage 1 for demo purposes");
        setApplicationStage(1); // Default to 1 for demo purposes
      }
    } catch (error) {
      console.error("Error fetching application stage:", error);
      console.log("Error occurred, defaulting to stage 1 for demo purposes");
      setApplicationStage(1); // Default to 1 for demo purposes
    }
  };

  // Fetch application stage for adequacy check visibility
  useEffect(() => {
    const fetchApplicationStage = async () => {
      if (!formData.appId) return;
      try {
        await getApplicationStage(formData.appId);
      } catch (error) {
        console.error("Error fetching application stage:", error);
        setApplicationStage(null);
      }
    };

    fetchApplicationStage();
  }, [formData.appId]);

  // Fetch facility details
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!formData.appId) return;

      try {
        setFacilitiesLoading(true);
        const response = await LaboratoryService.getFacilityDetails(formData.appId);
        if (response.success) {
          setFacilities(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching facility details:", error);
        setFacilities([]);
      } finally {
        setFacilitiesLoading(false);
      }
    };

    fetchFacilities();
  }, [formData.appId]);


  // Note: Tab index is now set directly when URL parameters are parsed
  // No need for applicationStage-based adjustment since tabs are always available

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle add new scope
  const handleAddScope = () => {
    // Load scope master data when opening modal
    loadScopeMasterData();
    setModalOpen(true);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchQuery(searchValue);
    setCurrentPage(1);
    loadScopeMasterData(1, searchValue);
  };

   const handleSearchSubGroup = (searchValue) => {
    setSelectedSubGroupMaster(searchValue);
    setCurrentPage(1);
    loadScopeMasterData(1, searchValue);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    // Optional: Clear selections when modal is closed
    // setSelectedScopes([]);
    // setSelectedScopeData([]);
  };

  // Filter existing scopes based on filter criteria
  const getFilteredExistingScopes = () => {
    return existingScopes
      .filter((scope) => {
        // PT Availability filter
        if (ptAvailabilityFilter && scope.PTAvailable !== ptAvailabilityFilter) {
          return false;
        }

        // Qualitative/Quantitative filter
        if (qualitativeQuantitativeFilter) {
          const scopeRangeType = scope.rangeType || "Quantitative";
          if (scopeRangeType !== qualitativeQuantitativeFilter) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        // Get test codes
        const aTestCode = (a.TestCode || "").trim().toUpperCase();
        const bTestCode = (b.TestCode || "").trim().toUpperCase();

        // Check if Test Code is blank, "0", or "NA"
        const aIsSpecial = !aTestCode || aTestCode === "0" || aTestCode === "NA" || aTestCode === "N/A";
        const bIsSpecial = !bTestCode || bTestCode === "0" || bTestCode === "NA" || bTestCode === "N/A";

        // Special test codes (blank, "0", "NA") come first at the TOP
        if (aIsSpecial && !bIsSpecial) return -1;
        if (!aIsSpecial && bIsSpecial) return 1;

        // If both are special, maintain original order by Id
        if (aIsSpecial && bIsSpecial) {
          return a.Id - b.Id;
        }

        // If both have regular test codes, group by test code (alphabetical)
        if (!aIsSpecial && !bIsSpecial) {
          const testCodeComparison = aTestCode.localeCompare(bTestCode);
          if (testCodeComparison !== 0) {
            return testCodeComparison;
          }
          // Same test code - sort by Id to keep them together in order
          return a.Id - b.Id;
        }

        return 0;
      });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadScopeMasterData(newPage, searchQuery);
  };

  // Handle scope selection
  const handleScopeSelection = (scopeId, isSelected) => {
    if (isSelected) {
      setSelectedScopes((prev) => [...prev, scopeId]);
      // Also store the complete scope data
      const scopeData = scopeMasterData.find((scope) => scope.Id === scopeId);
      if (scopeData) {
        setSelectedScopeData((prev) => [...prev, scopeData]);
      }
    } else {
      setSelectedScopes((prev) => prev.filter((id) => id !== scopeId));
      // Remove from selected scope data as well
      setSelectedScopeData((prev) => prev.filter((scope) => scope.Id !== scopeId));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageIds = scopeMasterData.map((scope) => scope.Id);
      setSelectedScopes((prev) => [...new Set([...prev, ...currentPageIds])]);
      // Add current page scope data to selected scope data (avoid duplicates)
      setSelectedScopeData((prev) => {
        const existingIds = prev.map(scope => scope.Id);
        const newScopes = scopeMasterData.filter(scope => !existingIds.includes(scope.Id));
        return [...prev, ...newScopes];
      });
    } else {
      // Deselect all from current page only
      const currentPageIds = scopeMasterData.map((scope) => scope.Id);
      setSelectedScopes((prev) => prev.filter(id => !currentPageIds.includes(id)));
      setSelectedScopeData((prev) => prev.filter(scope => !currentPageIds.includes(scope.Id)));
    }
  };

  // Add selected scopes to existing scopes
  const handleAddSelectedScopes = async () => {
    if (selectedScopes.length === 0) {
      showNotification("Please select at least one scope", "warning");
      return;
    }

    // Use the stored selectedScopeData which contains data from all pages
    const mainOrgId = localStorage.getItem("mainOrgId");
    setLoading(true);
    try {
      const result = await TestingLaboratoryService.saveSelectedScopes(
        selectedScopeData,
        formData.appId,
        mainOrgId || formData.orgId,
        formData.disciplineId,
        formData.groupId,
        parseInt(localStorage.getItem('labTypeId') || 1)
      );

      if (result.success) {
        showNotification(
          `Scope(s) added successfully`,
          "success"
        );
        setSelectedScopes([]);
        setSelectedScopeData([]); // Clear selected scope data as well
        setModalOpen(false);
        // Reload existing scopes to show the newly added ones
        loadExistingScopes();
      } else {
        showNotification(result.message || "Failed to save scopes", "error");
      }
    } catch (error) {
      console.error("Error saving selected scopes:", error);
      showNotification("Error saving scopes: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Load existing scopes from form153Scope table

  // Handle delete scope
  const handleDeleteScope = (scopeId) => {
    setSingleDeleteId(scopeId);
    setDeleteTarget("single");
    setDeleteConfirmOpen(true);
  };

  // Handle multiple delete
  const handleMultipleDelete = () => {
    setDeleteTarget("multiple");
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    try {
      setExistingScopesLoading(true);

      if (deleteTarget === "single") {
        const result = await TestingLaboratoryService.deleteExistingScope(
          singleDeleteId
        );
        if (result.success) {
          showNotification("Scope deleted successfully", "success");
          await loadExistingScopes();
        } else {
          showNotification(result.message || "Failed to delete scope", "error");
        }
      } else if (deleteTarget === "multiple") {
        // Delete multiple scopes
        let successCount = 0;
        let errorCount = 0;

        for (const scopeId of selectedExistingScopes) {
          try {
            const result = await TestingLaboratoryService.deleteExistingScope(scopeId);
            if (result.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          showNotification(
            `${successCount} scope(s) deleted successfully`,
            "success"
          );
          setSelectedExistingScopes([]);
          await loadExistingScopes();
        }

        if (errorCount > 0) {
          showNotification(
            `${errorCount} scope(s) could not be deleted`,
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error deleting scope:", error);
      showNotification(
        "Error deleting scope: " + (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setExistingScopesLoading(false);
      setDeleteConfirmOpen(false);
      setSingleDeleteId(null);
      setDeleteTarget(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSingleDeleteId(null);
    setDeleteTarget(null);
  };

  // Handle selection for delete
  const handleSelectForDelete = (scopeId, isSelected) => {
    if (isSelected) {
      setSelectedExistingScopes((prev) => [...prev, scopeId]);
    } else {
      setSelectedExistingScopes((prev) => prev.filter((id) => id !== scopeId));
    }
  };

  // Handle select all for delete (also used for binding since it's the same selection)
  const handleSelectAllForDelete = (isSelected) => {
    if (isSelected) {
      setSelectedExistingScopes(
        getFilteredExistingScopes().map((scope) => scope.Id)
      );
    } else {
      setSelectedExistingScopes([]);
    }
  };

  // Handle opening binding modal
  const handleOpenBindingModal = async () => {
    console.log(
      "handleOpenBindingModal called, selectedExistingScopes:",
      selectedExistingScopes
    );

    if (selectedExistingScopes.length === 0) {
      showNotification(
        "Please select at least one scope for binding",
        "warning"
      );
      return;
    }

    setBindingLoading(true);
    try {
      // Load binding data (PT programmes, equipment, reference materials)
      console.log("Loading binding data...");
      await loadBindingData();
      console.log("Binding data loaded, opening modal...");
      setBindingModalOpen(true);
    } catch (error) {
      console.error("Error loading binding data:", error);
      showNotification("Error loading binding data: " + error.message, "error");
      // Still open the modal even if data loading fails
      setBindingModalOpen(true);
    } finally {
      setBindingLoading(false);
    }
  };

  // Load binding data for the modal
  const loadBindingData = async () => {
    try {
      const appId = formData.appId;
      const orgId = formData.orgId;
      const disciplineId = formData.disciplineId;
      const labTypeId = localStorage.getItem("labTypeId") || "3";

      // Load each type of data with individual error handling
      let ptProgrammes = { data: [] };
      let equipment = { data: [] };
      let referenceMaterials = { data: [] };

      // Try to load PT programmes
      try {
        ptProgrammes = await TestingLaboratoryService.getPTProgrammes(
          appId,
          orgId,
          disciplineId,
          formData.groupId
        );
      } catch (error) {
        console.warn("PT programmes API not available:", error.message);
        // For now, use mock data
        ptProgrammes = {
          data: [
            { Id: 1, ProgramName: "PT Programme 1", Name: "Sample PT 1" },
            { Id: 2, ProgramName: "PT Programme 2", Name: "Sample PT 2" },
          ],
        };
      }

      // Try to load equipment
      try {
        equipment = await TestingLaboratoryService.getEquipmentMasterForDropdown(
          labTypeId,
          disciplineId,
          formData.groupId,
          appId,
          orgId
        );
      } catch (error) {
        console.warn("Equipment API error:", error.message);
        // For now, use mock data
        equipment = {
          data: [
            {
              Id: 1,
              equipmentname: "Sample Equipment 1",
              equipmentid: "EQ001",
            },
            {
              Id: 2,
              equipmentname: "Sample Equipment 2",
              equipmentid: "EQ002",
            },
          ],
        };
      }

      // Try to load reference materials
      try {
        referenceMaterials = await TestingLaboratoryService.getReferenceMaterials(
          appId,
          orgId,
          disciplineId,
          formData.groupId
        );
      } catch (error) {
        console.warn("Reference materials API error:", error.message);
        // For now, use mock data
        referenceMaterials = {
          data: [
            {
              Id: 1,
              MaterialName: "Reference Material 1",
              Name: "Sample Material 1",
            },
            {
              Id: 2,
              MaterialName: "Reference Material 2",
              Name: "Sample Material 2",
            },
          ],
        };
      }

      setBindingData({
        ptProgrammes: ptProgrammes.data || [],
        equipment: equipment.data || [],
        referenceMaterials: referenceMaterials.data || [],
      });
    } catch (error) {
      console.error("Error loading binding data:", error);
      // Set empty arrays as fallback
      setBindingData({
        ptProgrammes: [],
        equipment: [],
        referenceMaterials: [],
      });
    }
  };

  const selectedValues = (
    Array.isArray(editFormData.testPerformedAt)
      ? editFormData.testPerformedAt
      : (editFormData.testPerformedAt || "")
        .split(",")
        .map(v => v.trim())
        .filter(Boolean)
  );

  // Handle binding selection changes
  const handleBindingSelectionChange = (type, itemId, isSelected) => {
    setBindingSelections((prev) => ({
      ...prev,
      [type]: isSelected
        ? [...prev[type], itemId]
        : prev[type].filter((id) => id !== itemId),
    }));
  };

  // Search filter functions
  const handleSearchChange = (searchType, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [searchType]: value
    }));
  };

  // Filter PT Programmes based on search
  const filteredPTProgrammes = bindingData.ptProgrammes.filter(pt => {
    const searchTerm = searchFilters.ptSearch.toLowerCase();
    if (!searchTerm) return true;

    const programName = (pt.ProgramName || pt.participationIn || pt.Name || '').toLowerCase();
    const nodalLab = (pt.nodalLaboratory || '').toLowerCase();
    const ilcDetails = (pt.ilcDetails || '').toLowerCase();
    const remarks = (pt.remarks || '').toLowerCase();
    const provider = (pt.Provider || '').toLowerCase();

    return programName.includes(searchTerm) ||
      nodalLab.includes(searchTerm) ||
      ilcDetails.includes(searchTerm) ||
      remarks.includes(searchTerm) ||
      provider.includes(searchTerm);
  });

  // Filter Equipment based on search
  const filteredEquipment = bindingData.equipment.filter(equipment => {
    const searchTerm = searchFilters.equipmentSearch.toLowerCase();
    if (!searchTerm) return true;

    const equipmentName = (equipment.equipmentname || equipment.EquipmentName || equipment.Name || '').toLowerCase();
    const uid = (equipment.Uid || '').toLowerCase();

    return equipmentName.includes(searchTerm) || uid.includes(searchTerm);
  });

  // Filter Reference Materials based on search
  const filteredReferenceMaterials = bindingData.referenceMaterials.filter(material => {
    const searchTerm = searchFilters.referenceMaterialSearch.toLowerCase();
    if (!searchTerm) return true;

    const materialName = (material.MaterialName || material.Name || '').toLowerCase();
    const traceability = (material.Traceability || '').toLowerCase();

    return materialName.includes(searchTerm) || traceability.includes(searchTerm);
  });

  // Handle binding submit
  const handleSubmitBinding = async () => {
    try {
      setBindingLoading(true);

      const bindingRequest = {
        scopeIds: selectedExistingScopes,
        ptIds: bindingSelections.selectedPT,
        equipmentIds: bindingSelections.selectedEquipment,
        referenceMaterialIds: bindingSelections.selectedReferenceMaterial,
        appId: formData.appId,
        orgId: formData.orgId,
        disciplineId: formData.disciplineId,
        groupId: formData.groupId
      };

      // Debug logging
      console.log("Binding Request:", bindingRequest);
      console.log("Selected PT IDs:", bindingSelections.selectedPT);
      console.log("Available PT Programmes:", bindingData.ptProgrammes);

      await TestingLaboratoryService.bindScopeData(bindingRequest);

      showNotification(
        "Equipment, Reference Materials, and PT Programmes bound to scopes successfully!",
        "success"
      );

      // Close modal and reset selections
      setBindingModalOpen(false);
      setSelectedExistingScopes([]);
      setBindingSelections({
        selectedPT: [],
        selectedEquipment: [],
        selectedReferenceMaterial: [],
      });

      // Refresh existing scopes to show updated data
      await loadExistingScopes();
    } catch (error) {
      console.error("Error binding scope data:", error);
      showNotification(
        "Error binding data: " + (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setBindingLoading(false);
    }
  };

  // Handle edit scope
  const handleEditScope = (scope) => {
    setEditingScopeId(scope.Id);
    setEditFormData({
      rangeRemark: scope.rangeRemark || "",
      rangeType: scope.rangeType || "Quantitative",
      RangeLL: scope.RangeLL || "",
      RangeLLUnit: scope.RangeLLUnit || "",
      RangeUL: scope.RangeUL || "",
      RangeULUnit: scope.RangeULUnit || "",
      mu: scope.mu || "",
      cv: scope.cv || "",
      cvmu: scope.CVMU || scope.CVMu || scope.cvmu || "",
      testPerformedAt: scope.testPerformedAt || "",
      EquipmentName: scope.EquipmentName || "",
      Ref_Id: scope.Ref_Id || "",
      Pt_id: scope.Pt_id || "",
    });

    // Set read-only data for display
    setEditReadOnlyData({
      TestCode: scope.TestCode,
      disciplineName: formData.disciplineName,
      GroupTested: scope.GroupTested,
      TypeOfTest: scope.TypeOfTest,
      ScopeStandard: scope.ScopeStandard,
      PTAvailable: scope.PTAvailable,
    });

    setEditModalOpen(true);
  };

  // Handle update scope
  const handleUpdateScope = async () => {
    if (!editingScopeId) return;

    // Validate based on range type
    const rangeType = editFormData.rangeType || "Quantitative";

    if (rangeType === "Quantitative") {
      // Validate Quantitative fields
      if (
        !editFormData.RangeLL ||
        !editFormData.RangeUL ||
        !editFormData.RangeULUnit
      ) {
        setSnackbar({
          open: true,
          message:
            "For Quantitative type, Range Lower Limit, Range Upper Limit, and Unit of Measurement are required.",
          severity: "error",
        });
        return;
      }
      if (!editFormData.mu || !editFormData.cv) {
        setSnackbar({
          open: true,
          message:
            "For Quantitative type, @Observed Value and MU are required.",
          severity: "error",
        });
        return;
      }
      const rangeLL = parseFloat(editFormData.RangeLL);
      const rangeUL = parseFloat(editFormData.RangeUL);
      const cv = parseFloat(editFormData.cv);

      if (!editFormData.RangeLL || !editFormData.RangeUL || !editFormData.cv) {
        setSnackbar({
          open: true,
          message: "Range LL, Range UL and CV are required.",
          severity: "error",
        });
        return;
      }

      if (cv < rangeLL || cv > rangeUL) {
        setSnackbar({
          open: true,
          message: `@Observed Value must be between ${rangeLL} and ${rangeUL}.`,
          severity: "error",
        });
        return;
      }
    } else if (rangeType === "Qualitative" || rangeType === "Calculative" || rangeType === "Others") {
      // Validate Qualitative/Calculative/Others fields
      if (!editFormData.rangeRemark || !editFormData.rangeRemark.trim()) {
        setSnackbar({
          open: true,
          message: `For ${rangeType} type, Range Remarks is required.`,
          severity: "error",
        });
        return;
      }
    }

    setEditLoading(true);
    try {
      await TestingLaboratoryService.updateExistingScope(
        editingScopeId,
        editFormData.rangeRemark,
        editFormData.rangeType,
        editFormData.RangeLL,
        editFormData.RangeLLUnit,
        editFormData.RangeUL,
        editFormData.RangeULUnit,
        editFormData.mu,
        editFormData.cv,
        "", // mv - empty
        editFormData.testPerformedAt,
        editFormData.EquipmentName,
        editFormData.Ref_Id,
        editFormData.Pt_id
      );

      setSnackbar({
        open: true,
        message: "Scope updated successfully!",
        severity: "success",
      });

      // Refresh the existing scopes list
      await loadExistingScopes();

      // Close modal
      setEditModalOpen(false);
      setEditingScopeId(null);
      setEditFormData({});
      setEditReadOnlyData({});
    } catch (error) {
      console.error("Error updating scope:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error updating scope",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingScopeId(null);
    setEditFormData({});
    setEditReadOnlyData({});
  };

  // Adequacy Check Handlers
  const handleOpenAdequacyModal = useCallback((scopeId, scopeTitle) => {
    setCurrentAdequacyScopeId(scopeId);
    setCurrentAdequacyScopeTitle(scopeTitle);
    setAdequacyModalOpen(true);
  }, []);

  const handleCloseAdequacyModal = useCallback(() => {
    setAdequacyModalOpen(false);
    setCurrentAdequacyScopeId(null);
    setCurrentAdequacyScopeTitle("");
  }, []);

  const handleAdequacyChange = useCallback(
    (status) => {
      setAdequacyChecks((prev) => ({
        ...prev,
        [currentAdequacyScopeId]: {
          ...prev[currentAdequacyScopeId],
          status,
          remarks:
            status === "Adequate"
              ? ""
              : prev[currentAdequacyScopeId]?.remarks || "",
        },
      }));
    },
    [currentAdequacyScopeId]
  );

  const handleAdequacyRemarks = useCallback(
    (remarks) => {
      setAdequacyChecks((prev) => ({
        ...prev,
        [currentAdequacyScopeId]: {
          ...prev[currentAdequacyScopeId],
          remarks,
        },
      }));
    },
    [currentAdequacyScopeId]
  );

  const assessmentId = getAssessmentIdFromQuery();
  const assessorId = getAssessorIdFromQuery();

  const handleAdequacySubmit = useCallback(async () => {
    if (!currentAdequacyScopeId) return;

    const adequacy = adequacyChecks[currentAdequacyScopeId];
    if (
      !adequacy?.status ||
      (adequacy.status === "Inadequate" && !adequacy.remarks?.trim())
    ) {
      showNotification("Please fill all required fields", "error");
      return;
    }

    setAdequacyLoading(true);
    try {
      // Check if Record exists for this scope
      const existingRecord = dancRecords.find(
        (record) =>
          record.sourceid &&
          parseInt(record.sourceid) === parseInt(currentAdequacyScopeId)
      );

      // Determine NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');

      let ncType = 'Document Adequacy'; // Default

      if (userTypeId === '4') {
        ncType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        ncType = 'Assessment';
      }

      if (existingRecord) {

        // Prepare base update data
        const baseUpdateData = {
          subsectionName: "Scope Details",
          informationStatus: adequacy.status,
          observation:
            adequacy.status === "Inadequate"
              ? adequacy.remarks || "Document adequacy marked as Inadequate"
              : "Document adequacy check completed",
          NCtype: ncType,
          sourceid: currentAdequacyScopeId,
          isactive: 1,
        };

        // Add ClauseNo for Assessment users (userType 6) when clause is selected
        if (userTypeId === 6 && selectedClause) {
          baseUpdateData.ClauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
          console.log('📍 ScopeSection - Adding ClauseNo to update data:', baseUpdateData.ClauseNo);
        }

        console.log("Updating existing scope Record:", baseUpdateData);
        const response = await updateDANCBySourceId(
          formData.orgId,
          formData.appId,
          "Scope",
          currentAdequacyScopeId,
          assessmentId || 0,
          assessorId || localStorage.getItem('orgId'),
          baseUpdateData
        );

        if (response.success) {
          console.log("✅ Scope Record updated successfully");
          showNotification("Adequacy check updated successfully!", "success");
        } else {
          throw new Error(response.message || "Failed to Update");
        }
      } else {

        // Prepare base DANC data
        const dancData = {
          Orgid: parseInt(formData.orgId),
          Appid: parseInt(formData.appId),
          SectionName: "Scope",
          SubsectionName: "Scope Details",
          InformationProvided: adequacy.remarks || "",
          InformationStatus: adequacy.status,
          Observation:
            adequacy.status === "Inadequate"
              ? adequacy.remarks || "Document adequacy marked as Inadequate"
              : "Document adequacy check completed",
          sourceid: currentAdequacyScopeId,
          Insertedby: currentUserId,
          ClauseNo: selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause,
          assessmentId: parseInt(assessmentId) || 0,
          assessorId: parseInt(assessorId) || localStorage.getItem('orgId'),
          NCtype: ncType,
        };

        const response = await insertDANC(dancData);

        if (response.success) {
          console.log("✅ Scope Record created successfully");
          showNotification("Adequacy check submitted successfully!", "success");
        } else {
          throw new Error(response.message || "Failed to create Record");
        }
      }

      // Mark as submitted locally and include clause info for Assessment users
      setAdequacyChecks((prev) => {
        const updatedAdequacyCheck = {
          ...prev[currentAdequacyScopeId],
          isSubmitted: true,
          submittedAt: new Date().toISOString(),
        };

        // Add clause number for Assessment users
        if (userTypeId === 6 && selectedClause) {
          updatedAdequacyCheck.clauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
          console.log('📍 ScopeSection - Storing clause number in adequacy check:', updatedAdequacyCheck.clauseNo);
        }

        return {
          ...prev,
          [currentAdequacyScopeId]: updatedAdequacyCheck,
        };
      });

      // Trigger refresh
      setRefreshTrigger((prev) => prev + 1);

      setAdequacyModalOpen(false);
    } catch (error) {
      console.error("Error submitting scope adequacy check:", error);
      showNotification(
        "Error submitting adequacy check: " + error.message,
        "error"
      );
    } finally {
      setAdequacyLoading(false);
    }
  }, [
    currentAdequacyScopeId,
    adequacyChecks,
    dancRecords,
    formData.orgId,
    formData.appId,
    currentUserId,
  ]);

  // Bulk Adequacy Check Handlers
  const handleOpenBulkAdequacyModal = () => {
    if (selectedAdequacyScopes.length === 0) {
      showNotification(
        "Please select at least one scope for adequacy check",
        "warning"
      );
      return;
    }
    setBulkAdequacyModalOpen(true);
    fetchBulkScrutinyRemarks();
  };

  const fetchBulkScrutinyRemarks = async () => {
    setBulkLoadingRemarks(true);
    try {
      const responseApi = await getScrutinyRemarks({
        sectionName: 'Scope',
        type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
      });
      const response = responseApi.data;
      if (response.success && response.data) {
        setBulkScrutinyRemarks(response.data);
        console.log('Bulk Scope: Loaded scrutiny remarks:', response.data.length);
      } else {
        setBulkScrutinyRemarks([]);
      }
    } catch (error) {
      console.error('Error fetching bulk scrutiny remarks:', error);
      setBulkScrutinyRemarks([]);
    } finally {
      setBulkLoadingRemarks(false);
    }
  };

  const handleCloseBulkAdequacyModal = () => {
    setBulkAdequacyModalOpen(false);
    setBulkAdequacyStatus("");
    setBulkAdequacyRemarks("");
    setBulkScrutinyRemarks([]);
    setSelectedClause(null); // Clear selected clause when modal closes
    // Reset checkbox dropdown states
    setSelectedBulkRemarksOptions([]);
    setIsBulkCheckboxDropdownOpen(false);
  };

  // Checkbox dropdown handlers for bulk adequacy
  const handleBulkCheckboxChange = useCallback((option, isChecked) => {
    const optionText = typeof option === 'string' ? option : option.inadequate_remarks;

    if (isChecked) {
      setSelectedBulkRemarksOptions(prev => [...prev, option]);
    } else {
      setSelectedBulkRemarksOptions(prev =>
        prev.filter(item => {
          const itemText = typeof item === 'string' ? item : item.inadequate_remarks;
          return itemText !== optionText;
        })
      );
    }
  }, []);

  const handleClearAllBulkCheckboxes = useCallback(() => {
    setSelectedBulkRemarksOptions([]);
    setBulkAdequacyRemarks('');
  }, []);

  const handleBulkAdequacyRemarksChange = useCallback((newValue, isFromDropdown = false) => {
    setBulkAdequacyRemarks(newValue);

    if (!isFromDropdown && selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option =>
        typeof option === 'string' ? option : option.inadequate_remarks
      );

      if (!selectedTexts.some(text => newValue.includes(text))) {
        setSelectedBulkRemarksOptions([]);
      }
    }
  }, [selectedBulkRemarksOptions]);

  // Update remarks when checkbox selections change
  useEffect(() => {
    if (selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option =>
        typeof option === 'string' ? option : option.inadequate_remarks
      );

      const currentText = bulkAdequacyRemarks || '';
      const existingRemarks = currentText.split(',').map(s => s.trim()).filter(Boolean);

      const newRemarks = [...new Set([...existingRemarks, ...selectedTexts])];
      const updatedText = newRemarks.join(', ');

      if (updatedText !== currentText) {
        setBulkAdequacyRemarks(updatedText);
      }
    }
  }, [selectedBulkRemarksOptions, bulkAdequacyRemarks]);

  const handleBulkAdequacySubmit = async () => {
    if (
      !bulkAdequacyStatus ||
      (bulkAdequacyStatus === "Inadequate" && !bulkAdequacyRemarks?.trim()) ||
      (bulkAdequacyStatus === "Inadequate" && userTypeId === '6' && !selectedClause)
    ) {
      showNotification(
        userTypeId === '6' && bulkAdequacyStatus === "Inadequate" && !selectedClause
          ? "Please select a clause number for Assessment inadequate status"
          : "Please fill all required fields",
        "error"
      );
      return;
    }

    setBulkAdequacyLoading(true);
    try {


      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Determine NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');
      let ncType = 'Document Adequacy'; // Default

      if (userTypeId === '4') {
        ncType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        ncType = 'Assessment';
      }

      console.log(`🔍 Bulk scope adequacy submit for userType ${userTypeId} with NCtype: ${ncType}`);

      // Process each selected scope
      for (const scopeId of selectedAdequacyScopes) {
        try {
          // Check if Record exists for this scope
          const existingRecord = dancRecords.find(
            (record) =>
              record.sourceid && parseInt(record.sourceid) === parseInt(scopeId)
          );

          if (existingRecord) {
            // Update existing Record
            const updateData = {
              subsectionName: "Scope Details",
              informationStatus: bulkAdequacyStatus,
              observation:
                bulkAdequacyStatus === "Inadequate"
                  ? bulkAdequacyRemarks ||
                  "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              NCtype: ncType,
              sourceid: scopeId,
              isactive: 1,
            };

            // Add ClauseNo for Assessment users (userType 6) when clause is selected
            if (userTypeId === '6' && selectedClause) {
              updateData.ClauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
              console.log('📍 ScopeSection - Adding ClauseNo to bulk Update data:', updateData.ClauseNo);
            }

            console.log(
              `Updating Record for scope ${scopeId}:`,
              updateData
            );
            const response = await updateDANCBySourceId(
              formData.orgId,
              formData.appId,
              "Scope",
              scopeId,
              assessmentId || 0,
              assessorId || localStorage.getItem('orgId'),
              updateData
            );

            if (response.success) {
              console.log(`✅ Record updated for scope ${scopeId}`);
              successCount++;
            } else {
              throw new Error(
                response.message || "Failed to Update"
              );
            }
          } else {
            const baseBulkDANCData = {
              Orgid: parseInt(formData.orgId),
              Appid: parseInt(formData.appId),
              SectionName: "Scope",
              SubsectionName: "Scope Details",
              InformationProvided: bulkAdequacyRemarks || "",
              InformationStatus: bulkAdequacyStatus,
              Observation:
                bulkAdequacyStatus === "Inadequate"
                  ? bulkAdequacyRemarks ||
                  "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              sourceid: scopeId,
              Insertedby: currentUserId,
              NCtype: ncType,
            };

            // Add ClauseNo for Assessment users (userType 6) when clause is selected
            if (userTypeId === '6' && selectedClause) {
              baseBulkDANCData.ClauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
              console.log('📍 ScopeSection - Adding ClauseNo to bulk DANC data:', baseBulkDANCData.ClauseNo);
            }

            // Add assessment parameters for Assessment users
            const dancData = userTypeId === '6'
              ? {
                ...baseBulkDANCData,
                assessmentId: getAssessmentIdFromQuery(),
                assessorId: getAssessorIdFromQuery()
              }
              : baseBulkDANCData;

            console.log(
              `Creating Record for scope ${scopeId}:`,
              dancData
            );
            const response = await insertDANC(dancData);

            if (response.success) {
              console.log(`✅ Record created for scope ${scopeId}`);
              successCount++;
            } else {
              throw new Error(
                response.message || "Failed to create Record"
              );
            }
          }
        } catch (error) {
          console.error(`Error processing scope ${scopeId}:`, error);
          errors.push(`Scope ${scopeId}: ${error.message}`);
          errorCount++;
        }
      }

      // Update adequacy checks locally for all selected scopes
      const updatedAdequacyChecks = { ...adequacyChecks };
      selectedAdequacyScopes.forEach((scopeId) => {
        const adequacyUpdate = {
          status: bulkAdequacyStatus,
          remarks: bulkAdequacyRemarks,
          isSubmitted: true,
          submittedAt: new Date().toISOString(),
        };

        // Add clause number for Assessment users
        if (userTypeId === '6' && selectedClause) {
          adequacyUpdate.clauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
          console.log('📍 ScopeSection - Storing clause number in bulk adequacy check:', adequacyUpdate.clauseNo);
        }

        updatedAdequacyChecks[scopeId] = adequacyUpdate;
      });
      setAdequacyChecks(updatedAdequacyChecks);

      // Show results
      if (successCount > 0) {
        showNotification(
          `Bulk adequacy check completed: ${successCount} scope(s) processed successfully${errorCount > 0 ? `, ${errorCount} failed` : ""
          }`,
          errorCount > 0 ? "warning" : "success"
        );
      }

      if (errorCount > 0 && errors.length > 0) {
        console.error("Bulk adequacy submission errors:", errors);
      }

      // Trigger refresh to reload DA-NC data
      setRefreshTrigger((prev) => prev + 1);

      // Clear selections and close modal
      setSelectedAdequacyScopes([]);
      handleCloseBulkAdequacyModal();
    } catch (error) {
      console.error("Error submitting bulk adequacy check:", error);
      showNotification(
        "Error submitting bulk adequacy check: " + error.message,
        "error"
      );
    } finally {
      setBulkAdequacyLoading(false);
    }
  };

  const handleAdequacyScopeSelection = (scopeId, isSelected) => {
    if (isSelected) {
      setSelectedAdequacyScopes((prev) => [...prev, scopeId]);
    } else {
      setSelectedAdequacyScopes((prev) => prev.filter((id) => id !== scopeId));
    }
  };

  const handleSelectAllAdequacyScopes = (isSelected) => {
    if (isSelected) {
      setSelectedAdequacyScopes(
        getFilteredExistingScopes().map((scope) => scope.Id)
      );
    } else {
      setSelectedAdequacyScopes([]);
    }
  };

  // Test Witness Handlers
  const handleTestWitnessScopeSelection = (scopeId, isSelected) => {
    if (isSelected) {
      setSelectedTestWitnessScopes((prev) => [...prev, scopeId]);
    } else {
      setSelectedTestWitnessScopes((prev) => prev.filter((id) => id !== scopeId));
    }
  };

  const handleSelectAllTestWitnessScopes = (isSelected) => {
    if (isSelected) {
      // Only select scopes that are not already marked for test witness
      const availableScopes = getFilteredExistingScopes()
        .filter((scope) => scope.Istestwitness !== 1)
        .map((scope) => scope.Id);
      setSelectedTestWitnessScopes(availableScopes);
    } else {
      setSelectedTestWitnessScopes([]);
    }
  };

  const handleOpenTestWitnessModal = () => {
    if (selectedTestWitnessScopes.length === 0) {
      showNotification("Please select at least one scope for test witness", "warning");
      return;
    }
    setTestWitnessConfirmOpen(true);
  };

  const handleCloseTestWitnessModal = () => {
    setTestWitnessConfirmOpen(false);
  };

  const handleConfirmTestWitness = async () => {
    if (selectedTestWitnessScopes.length === 0) {
      showNotification("No scopes selected", "warning");
      return;
    }

    setTestWitnessLoading(true);
    try {
      const response = await TestingLaboratoryService.markTestWitness({
        scopeIds: selectedTestWitnessScopes,
        appId: formData.appId,
        orgId: formData.orgId
      });

      if (response.success) {
        showNotification(
          `Successfully marked ${selectedTestWitnessScopes.length} scope(s) for test witness`,
          "success"
        );

        // Refresh existing scopes to show updated status
        loadExistingScopes();

        // Clear selections and close modal
        setSelectedTestWitnessScopes([]);
        handleCloseTestWitnessModal();
      } else {
        throw new Error(response.message || "Failed to mark scopes for test witness");
      }
    } catch (error) {
      console.error("Error marking scopes for test witness:", error);
      showNotification(
        "Error marking scopes for test witness: " + error.message,
        "error"
      );
    } finally {
      setTestWitnessLoading(false);
    }
  };

  // Handle unmark test witness for individual scope
  const handleUnmarkTestWitness = async (scopeId, scopeName) => {
    if (window.confirm(`Are you sure you want to unmark test witness for "${scopeName}"?`)) {
      try {
        const response = await TestingLaboratoryService.unmarkTestWitness(
          scopeId,
          formData.appId,
          formData.orgId
        );

        if (response.success) {
          showNotification("Successfully unmarked test witness", "success");
          // Refresh existing scopes to show updated status
          loadExistingScopes();
        } else {
          throw new Error(response.message || "Failed to unmark test witness");
        }
      } catch (error) {
        console.error("Error unmarking test witness:", error);
        showNotification(
          "Error unmarking test witness: " + error.message,
          "error"
        );
      }
    }
  };

  // ==================== PERFORMANCE SCORE HANDLERS ====================

  // Open performance score dialog for a specific PT
  const handleOpenPerformanceScoreDialog = async (scope, ptId, ptName) => {
    setSelectedPtForScore({
      scopeId: scope.Id,
      scopeName: `${scope.GroupTested} - ${scope.TypeOfTest}`,
      ptId: ptId,
      ptName: ptName,
      appId: scope.AppId,
      orgId: scope.OrgId,
      labTypeId: formData.labTypeId || 1,
    });

    // Fetch existing performance scores for this scope and PT
    try {
      const response = await TestingLaboratoryService.getPerformanceScores({
        labTypeId: formData.labTypeId || 1,
        scopeId: scope.Id,
      });

      if (response.success && response.data) {
        // Filter for this specific PT
        const ptScores = response.data.filter(
          (score) => score.ptId === ptId || score.ptId === parseInt(ptId)
        );
        setExistingPerformanceScores(ptScores);

        // If there's existing data, populate the form
        if (ptScores.length > 0) {
          const latestScore = ptScores[0];
          setPerformanceScoreForm({
            labCode: latestScore.labCode || "",
            performanceScore: latestScore.performanceScore || "",
            editId: latestScore.id, // Store ID for update
          });
        } else {
          setPerformanceScoreForm({
            labCode: "",
            performanceScore: "",
            editId: null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching performance scores:", error);
      setPerformanceScoreForm({
        labCode: "",
        performanceScore: "",
        editId: null,
      });
      setExistingPerformanceScores([]);
    }

    setPerformanceScoreDialogOpen(true);
  };

  // Close performance score dialog
  const handleClosePerformanceScoreDialog = () => {
    setPerformanceScoreDialogOpen(false);
    setSelectedPtForScore(null);
    setPerformanceScoreForm({
      labCode: "",
      performanceScore: "",
      editId: null,
    });
    setExistingPerformanceScores([]);
  };

  // Handle performance score form change
  const handlePerformanceScoreChange = (field, value) => {
    setPerformanceScoreForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Save performance score
  const handleSavePerformanceScore = async () => {
    if (!performanceScoreForm.labCode || !performanceScoreForm.performanceScore) {
      showNotification("Please enter both Lab Code and Performance Score", "error");
      return;
    }

    if (!selectedPtForScore) {
      showNotification("No PT selected", "error");
      return;
    }

    setPerformanceScoreLoading(true);

    try {
      const payload = {
        appId: selectedPtForScore.appId,
        orgId: selectedPtForScore.orgId,
        scopeId: selectedPtForScore.scopeId,
        ptId: selectedPtForScore.ptId,
        labTypeId: selectedPtForScore.labTypeId,
        labCode: performanceScoreForm.labCode,
        performanceScore: performanceScoreForm.performanceScore,
        insertedBy: localStorage.getItem("orgId") || 0,
      };

      let response;
      if (performanceScoreForm.editId) {
        // Update existing performance score
        response = await TestingLaboratoryService.updatePerformanceScore(
          performanceScoreForm.editId,
          {
            labCode: performanceScoreForm.labCode,
            performanceScore: performanceScoreForm.performanceScore,
            labTypeId: selectedPtForScore.labTypeId,
          }
        );
      } else {
        // Add new performance score
        response = await TestingLaboratoryService.addPerformanceScore(payload);
      }

      if (response.success) {
        showNotification(
          performanceScoreForm.editId
            ? "Performance score updated successfully"
            : "Performance score added successfully",
          "success"
        );
        handleClosePerformanceScoreDialog();
        // Optionally refresh the scopes to show updated data
        loadExistingScopes();
      } else {
        throw new Error(response.message || "Failed to save performance score");
      }
    } catch (error) {
      console.error("Error saving performance score:", error);
      showNotification(
        "Error saving performance score: " + error.message,
        "error"
      );
    } finally {
      setPerformanceScoreLoading(false);
    }
  };

  // Helper functions for column types
  const isNonEditableColumn = (header) => {
    const nonEditableColumns = [
      "id",
      "test code",
      "testcode",
      "srno",
      "sr no",
      "sr.no",
      "discipline name",
      "disciplinename",
      "materials/products tested",
      "group tested",
      "grouptested",
      "materialstested",
      "productstested",
      "specific test performed",
      "type of test",
      "typeoftest",
      "specifictestperformed",
      "testperformed",
      "test method / equipment used",
      "scope standard",
      "scopestandard",
      "testmethod",
      "equipmentused",
      "testmethodequipmentused",
    ];
    return nonEditableColumns.some((col) =>
      header?.toLowerCase().includes(col.toLowerCase())
    );
  };

  const isDropdownColumn = (header) => {
    const dropdownColumns = [
      // "test performed at",
      // "testperformedat",
      // "facility type",
      "location",
      "range type",
      "rangetype",
    ];
    return dropdownColumns.some((col) =>
      header?.toLowerCase().includes(col.toLowerCase())
    );
  };

  const isFacilityColumn = (header) => {
    const headerLower = header?.toLowerCase() || "";

    headerLower.includes("facility type") ||
      headerLower.includes("test performed at") ||
      headerLower.includes("testperformedat");

  }

  const isNumericColumn = (header) => {
    const headerLower = header?.toLowerCase() || "";

    // Only Range LL and Range UL (not their unit columns) should be numeric
    const isRangeLL =
      (headerLower.includes("range ll") ||
        headerLower.includes("rangell") ||
        headerLower.includes("range lower limit") ||
        headerLower.includes("rangelowerlimit")) &&
      !headerLower.includes("unit");

    const isRangeUL =
      (headerLower.includes("range ul") ||
        headerLower.includes("rangeul") ||
        headerLower.includes("range upper limit") ||
        headerLower.includes("rangeupperlimit")) &&
      !headerLower.includes("unit");

    return isRangeLL || isRangeUL;
  };

  const isDateColumn = (header) => {
    const dateColumns = ["date", "validity", "expiry"];
    return dateColumns.some((col) =>
      header?.toLowerCase().includes(col.toLowerCase())
    );
  };

  const isRequiredColumn = (header) => {
    const requiredColumns = [
      // Always required fields
      "range type",
      "rangetype",
      // Conditionally required fields for Quantitative
      "range ll",
      "rangell",
      "range lower limit",
      "rangelowerlimit",
      //"range ll unit",
      "rangellunit",
      "range lower limit unit",
      "range ul",
      "rangeul",
      "range upper limit",
      "rangeupperlimit",
      "range ul unit",
      "rangeulunit",
      "range upper limit unit",
      // Conditionally required fields for Qualitative and Calculative
      "range remarks",
      "rangeremarks",
      "qualitative remarks",
      "qualitativeremarks",
      "calculative remarks",
      "calculativeremarks",
      "remarks",
      "range remark",
      "rangeremark",
    ];
    return requiredColumns.some((col) =>
      header?.toLowerCase().includes(col.toLowerCase())
    );
  };

  const handleRowSelect = (index, checked) => {
    if (checked) {
      setSelectedRows(prev => [...prev, index]);
    } else {
      setSelectedRows(prev => prev.filter(i => i !== index));
    }
  };

  const applyFacilityToSelected = () => {
    const facilityIndex = previewHeaders.findIndex(
      (h) =>
        h?.toLowerCase().includes("facility type") ||
        h?.toLowerCase().includes("test performed at")
    );

    if (facilityIndex === -1) return;

    const updated = editedPreviewData.map((row, index) => {
      if (selectedRows.includes(index)) {
        const newRow = [...row];

        // ✅ FIX: convert array → string
        newRow[facilityIndex] = Array.isArray(commonFacilityType)
          ? commonFacilityType.join(", ")
          : commonFacilityType;

        return newRow;
      }
      return row;
    });

    setEditedPreviewData(updated);
  };


  const handleSelectAllRows = (checked) => {
    if (checked) {
      setSelectedRows(editedPreviewData.map((_, i) => i));
    } else {
      setSelectedRows([]);
    }
  };


  // Helper function to check if column should be hidden from bulk Update preview
  const isHiddenFromPreview = (header) => {
    const hiddenColumns = [
      "insert date",
      "insertdate",
      "created date",
      "createddate",
      "timestamp",
      "created at",
      "updated at",
      "modified date",
      "equipment",
      "equipmentname",
      "equipment name",
      "reference material",
      "referencematerial",
      "ref_id",
      "reference",
      "proficiency testing",
      "proficiencytesting",
      "pt_id",
      "pt id",
      "pt available",
      "ptavailable",
      "pt availability",
      "range / detection limit",
      "range detection limit",
      "rangedetectionlimit",
    ];
    return hiddenColumns.some((col) =>
      header?.toLowerCase().includes(col.toLowerCase())
    );
  };

  // Helper function to reorder headers so Range Remarks comes after Range Type
  const reorderHeadersForPreview = (headers) => {
    const reorderedHeaders = [...headers];

    // Find Range Type and Range Remarks columns
    const rangeTypeIndex = reorderedHeaders.findIndex(
      (header) =>
        header?.toLowerCase().includes("range type") ||
        header?.toLowerCase().includes("rangetype") ||
        header?.toLowerCase().includes("qualitative/quantitative") ||
        header?.toLowerCase().includes("qualitativequantitative")
    );

    const rangeRemarksIndex = reorderedHeaders.findIndex(
      (header) =>
        header?.toLowerCase().includes("range remarks") ||
        header?.toLowerCase().includes("rangeremarks") ||
        header?.toLowerCase().includes("qualitative remarks") ||
        header?.toLowerCase().includes("qualitativeremarks")
    );

    // If both columns exist and Range Remarks is not already after Range Type
    if (
      rangeTypeIndex !== -1 &&
      rangeRemarksIndex !== -1 &&
      rangeRemarksIndex !== rangeTypeIndex + 1
    ) {
      // Remove Range Remarks from its current position
      const rangeRemarksHeader = reorderedHeaders.splice(
        rangeRemarksIndex,
        1
      )[0];

      // Adjust Range Type index if Range Remarks was before it
      const adjustedRangeTypeIndex =
        rangeRemarksIndex < rangeTypeIndex
          ? rangeTypeIndex - 1
          : rangeTypeIndex;

      // Insert Range Remarks right after Range Type
      reorderedHeaders.splice(
        adjustedRangeTypeIndex + 1,
        0,
        rangeRemarksHeader
      );
    }

    return reorderedHeaders;
  };

  // Helper function to reorder data according to reordered headers
  const reorderDataForPreview = (
    dataRows,
    originalHeaders,
    reorderedHeaders
  ) => {
    if (originalHeaders.length === 0 || reorderedHeaders.length === 0) {
      return dataRows;
    }

    // Create mapping from original header positions to new positions
    const headerMapping = {};
    originalHeaders.forEach((header, originalIndex) => {
      const newIndex = reorderedHeaders.findIndex((h) => h === header);
      if (newIndex !== -1) {
        headerMapping[originalIndex] = newIndex;
      }
    });

    // Reorder each data row according to the header mapping
    return dataRows.map((row) => {
      const reorderedRow = new Array(reorderedHeaders.length).fill("");
      row.forEach((cell, originalIndex) => {
        const newIndex = headerMapping[originalIndex];
        if (newIndex !== undefined) {
          reorderedRow[newIndex] = cell;
        }
      });
      return reorderedRow;
    });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toISOString().split("T")[0];
  };

  // Handle cell value change
  const handleCellValueChange = (rowIndex, colIndex, value) => {
    console.log("🔄 Cell value change:", {
      rowIndex,
      colIndex,
      value,
      header: previewHeaders[colIndex],
    });

    // Prevent changes to non-editable columns
    if (
      colIndex < previewHeaders.length &&
      isNonEditableColumn(previewHeaders[colIndex])
    ) {
      console.warn(
        "❌ Attempted to edit non-editable column:",
        previewHeaders[colIndex]
      );
      return;
    }

    // Create a deep copy of the data to ensure React re-renders
    const newData = editedPreviewData.map((row, rIndex) => {
      if (rIndex === rowIndex) {
        const newRow = [...row];
        newRow[colIndex] = value;
        console.log("✅ Updated row:", {
          oldValue: row[colIndex],
          newValue: value,
          rowIndex,
          colIndex,
        });
        return newRow;
      }
      return [...row];
    });

    setEditedPreviewData(newData);
    setHasUnsavedChanges(true);

    // Clear specific field error when user starts typing
    if (validationErrors[rowIndex] && validationErrors[rowIndex][colIndex]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[rowIndex][colIndex];

      // If no more errors for this row, remove the row entry
      if (Object.keys(newValidationErrors[rowIndex]).length === 0) {
        delete newValidationErrors[rowIndex];
      }

      setValidationErrors(newValidationErrors);
    }
  };

  // Validate edited data
  // Validate edited data
  const validateEditedData = () => {
    const errors = {};
    const errorMessages = [];

    editedPreviewData.forEach((row, rowIndex) => {
      // Find the range type column index
      const rangeTypeColIndex = previewHeaders.findIndex(
        (header) =>
          header?.toLowerCase().includes("range type") ||
          header?.toLowerCase().includes("rangetype")
      );

      const rangeType = rangeTypeColIndex !== -1 ? row[rangeTypeColIndex] : "";

      previewHeaders.forEach((header, colIndex) => {
        const value = row[colIndex];
        const isEmpty = !value || value.toString().trim() === "";
        const headerLower = header?.toLowerCase() || "";

        // Always required fields
        const alwaysRequired =
          headerLower.includes("range type") ||
          headerLower.includes("rangetype");

        // Conditionally required for Quantitative
        const quantitativeRequired =
          rangeType === "Quantitative" &&
          (headerLower.includes("range ll") ||
            headerLower.includes("rangell") ||
            headerLower.includes("range lower limit") ||
            headerLower.includes("rangelowerlimit") ||
            //headerLower.includes("range ll unit") ||
            headerLower.includes("rangellunit") ||
            headerLower.includes("range ul") ||
            headerLower.includes("rangeul") ||
            headerLower.includes("range upper limit") ||
            headerLower.includes("rangeupperlimit") ||
            headerLower.includes("range ul unit") ||
            headerLower.includes("Unit of measurement") ||
            headerLower.includes("rangeulunit") ||
            headerLower.includes("@Observation Value"));

        // Conditionally required for Qualitative, Calculative, and Others
        const qualitativeRequired =
          (rangeType === "Qualitative" ||
            rangeType === "Calculative" ||
            rangeType === "Others") &&
          (headerLower.includes("range remarks") ||
            headerLower.includes("rangeremarks") ||
            headerLower.includes("qualitative remarks") ||
            headerLower.includes("qualitativeremarks") ||
            headerLower.includes("calculative remarks") ||
            headerLower.includes("calculativeremarks") ||
            (headerLower.includes("remarks") &&
              !headerLower.includes("range")));

        // Check if dropdown fields are required (Range Type and Facility Type should always be selected)
        const isDropdownField =
          headerLower.includes("range type") ||
          headerLower.includes("rangetype") ||
          // headerLower.includes("facility type") ||
          headerLower.includes("test performed at") ||
          headerLower.includes("testperformedat");

        // Validate dropdown values are from valid options
        const isValidDropdownValue = (() => {
          if (!isDropdownField || isEmpty) return true; // Skip validation if empty (will be caught by required check)

          const validFacilityTypes = [
            "PermanentFacility",
            "TemporaryFacility",
            "SiteOfCustomer",
          ];
          const validRangeTypes = [
            "Quantitative",
            "Qualitative",
            "Calculative",
            "Others",
          ];

          if (
            headerLower.includes("range type") ||
            headerLower.includes("rangetype")
          ) {
            return validRangeTypes.includes(value);
          } else if (
            headerLower.includes("test performed at") ||
            headerLower.includes("facility type") ||
            headerLower.includes("test performed at") ||
            headerLower.includes("testperformedat")
          ) {
            const enteredValues = value
              .split(",")
              .map((x) => x.trim().toLowerCase())
              .filter(Boolean);

            return enteredValues.every((item) =>
              validFacilityTypes.includes(item)
            );
          }

          return true;
        })();

        // Check if field is required or has invalid dropdown value
        if (
          ((alwaysRequired ||
            quantitativeRequired ||
            qualitativeRequired ||
            isDropdownField) &&
            isEmpty) ||
          (isDropdownField && !isEmpty && !isValidDropdownValue)
        ) {
          if (!errors[rowIndex]) errors[rowIndex] = {};

          let errorMsg = "This field is required";

          if (isDropdownField && !isEmpty && !isValidDropdownValue) {
            if (headerLower.includes("range type")) {
              errorMsg = `Invalid Range Type: "${value}". Must be one of: Quantitative, Qualitative, Calculative, Others`;
            } else if (
              headerLower.includes("facility type") ||
              headerLower.includes("testperformedat")
            ) {
              errorMsg =
                `Invalid Facility Type: "${value}". ` +
                `Enter only valid comma separated values from dropdown`;
            } else {
              errorMsg = `Invalid dropdown value: "${value}"`;
            }
          } else if (quantitativeRequired) {
            errorMsg = "Required for Quantitative range type";
          } else if (qualitativeRequired) {
            errorMsg =
              rangeType === "Calculative"
                ? "Required for Calculative range type"
                : rangeType === "Others"
                  ? "Required for Others range type"
                  : "Required for Qualitative range type";
          } else if (isDropdownField) {
            errorMsg = headerLower.includes("range type")
              ? "Range Type selection is required"
              // : headerLower.includes("facility type") ||
              : headerLower.includes("test performed at")
                ? "Facility Type selection is required"
                : "Selection is required";
          }

          errors[rowIndex][colIndex] = errorMsg;
          errorMessages.push(`Row ${rowIndex + 1}, ${header}: ${errorMsg}`);
        }
      });
    });

    setValidationErrors(errors);
    return errorMessages;
  };

  // Handle preview modal close
  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewData([]);
    setPreviewHeaders([]);
    setPreviewFileName("");
    setUploadFile(null);
    setEditedPreviewData([]);
    setOriginalPreviewData([]);
    setHasUnsavedChanges(false);
    setValidationErrors({});
    // Reset file input
    const fileInput = document.getElementById("bulk-upload-input");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Handle save edited data
  const handleSaveEdit = () => {
    // Clear previous validation errors
    setValidationErrors({});

    const validationErrorMessages = validateEditedData();

    if (validationErrorMessages.length > 0) {
      showNotification(
        `Please fix ${validationErrorMessages.length} validation errors before saving.`,
        "error"
      );
      return;
    }

    // Mark changes as saved
    setHasUnsavedChanges(false);
    showNotification(
      "✅ Data validated successfully! All required fields are properly filled.",
      "success"
    );
  };

  // Handle file upload and show preview
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
      showNotification(`File "${file.name}" selected for upload`, "info");

      // Show preview of the uploaded file
      try {
        setPreviewLoading(true);

        // Read the Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "buffer" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!jsonData || jsonData.length === 0) {
          showNotification("No data found in the uploaded file", "warning");
          return;
        }

        // Extract headers from first row
        const headers = jsonData[0] || [];
        const dataRows = jsonData.slice(1);

        // Reorder headers to ensure Range Remarks comes after Range Type
        const reorderedHeaders = reorderHeadersForPreview(headers);
        const reorderedData = reorderDataForPreview(
          dataRows,
          headers,
          reorderedHeaders
        );

        // Set file name and headers for editable preview
        setPreviewFileName(file.name);
        setPreviewHeaders(reorderedHeaders);

        // Convert to 2D array format for editing
        const editableData = reorderedData.map((row) => {
          // Ensure row has same length as reordered headers
          const paddedRow = [...row];
          while (paddedRow.length < reorderedHeaders.length) {
            paddedRow.push("");
          }
          return paddedRow.map((cell) =>
            cell === undefined || cell === null ? "" : String(cell)
          );
        });

        setEditedPreviewData(editableData);
        setOriginalPreviewData(editableData.map((row) => [...row])); // Store original data

        // Also prepare processed data for backward compatibility
        const processedData = reorderedData.map((row, index) => {
          const rowObj = {};
          reorderedHeaders.forEach((header, i) => {
            rowObj[header] = row[i] || "";
          });

          return {
            rowNumber: index + 1,
            id: rowObj["ID"] || "",
            testCode: rowObj["Test Code"] || "",
            groupTested:
              rowObj["Materials/Products Tested"] ||
              rowObj["Group Tested"] ||
              "",
            typeOfTest:
              rowObj["Specific Test Performed"] || rowObj["Type of Test"] || "",
            scopeStandard:
              rowObj["Test Method / Equipment Used"] ||
              rowObj["Scope Standard"] ||
              "",
            rangeType: rowObj["Range Type"] || "Quantitative",
            rangeLL: rowObj["Range LL"] ? parseFloat(rowObj["Range LL"]) : null,
            //rangeLLUnit: rowObj["Range LL Unit"] || "",
            rangeUL: rowObj["Range UL"] ? parseFloat(rowObj["Range UL"]) : null,
            //rangeULUnit: rowObj["Range UL Unit"] || "",
            rangeULUnit: rowObj["Unit of measurement"] || "",
            mu: rowObj["Mu"] ? parseFloat(rowObj["Mu"]) : null,
            //cv: rowObj["CV"] || rowObj["Cv"] || "",
            cv: rowObj["@Observed Value"] ? rowObj["@Observed Value"] : '',
            testPerformedAt:
              rowObj["Facility Type"] ||
              rowObj["Test Performed At"] ||
              "PermanentSiteFacility",
            ptAvailable: rowObj["PT Available"] || "",
            rangeRemark: rowObj["Range Remarks"] || "",
            // Validation flags
            hasTestCode: !!(rowObj["Test Code"] || "").trim(),
            hasRequiredFields:
              !!(
                rowObj["Materials/Products Tested"] ||
                rowObj["Group Tested"] ||
                ""
              ).trim() &&
              !!(
                rowObj["Specific Test Performed"] ||
                rowObj["Type of Test"] ||
                ""
              ).trim(),
          };
        });

        setPreviewData(processedData);
        setHasUnsavedChanges(true); // Show save button from start
        setValidationErrors({});
        setPreviewModalOpen(true);

        // Run validation immediately to show errors for invalid Excel values
        setTimeout(() => {
          validateEditedData();
        }, 100);
      } catch (error) {
        console.error("Error reading Excel file:", error);
        showNotification(
          "Failed to read Excel file. Please check the file format.",
          "error"
        );
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  // Confirm and submit bulk Update from preview
  const confirmBulkUpdate = async () => {
    // Check if we have edited preview data
    if (editedPreviewData.length === 0) {
      showNotification(
        "No preview data available. Please parse a file first.",
        "warning"
      );
      return;
    }

    // Check for unsaved changes
    if (hasUnsavedChanges) {
      showNotification("Please save your changes before uploading.", "warning");
      return;
    }

    // Validate all dropdown values before saving
    const validationErrors = validateEditedData();
    if (validationErrors.length > 0) {
      showNotification(
        `Cannot save: ${validationErrors.length} validation errors found. Please fix all dropdown values and required fields.`,
        "error"
      );
      return;
    }

    setLoading(true);

    try {
      // Get required parameters
      const appId = localStorage.getItem("appId");
      const orgId = localStorage.getItem("mainOrgId") || localStorage.getItem("orgId");
      // Use decrypted disciplineId from formData instead of raw encrypted one from URL
      const disciplineId = formData.disciplineId;
      const groupId = formData.groupId;

      // Debug: Validate decrypted parameters
      console.log('📝 Scope Bulk Update - Parameters Check:', {
        appId,
        orgId,
        disciplineId,
        disciplineName: formData.disciplineName
      });

      if (!appId || !orgId || !disciplineId || !groupId) {
        console.error('❌ Scope Bulk Update - Missing required parameters:', { appId, orgId, disciplineId });
        showNotification(
          "Missing application parameters. Please navigate back from discipline section.",
          "error"
        );
        return;
      }

      showNotification("Processing bulk Update...", "info");

      // Use the edited preview data
      const headers = previewHeaders;
      const dataRows = editedPreviewData;

      console.log("Using edited preview data:");
      console.log("Headers:", headers);
      console.log("Data rows count:", dataRows.length);

      // Define expected column mapping (case-insensitive)
      const columnMapping = {
        id: "id",
        "test code": "testCode",
        testcode: "testCode",
        "materials/products tested": "groupTested",
        "group tested": "groupTested",
        grouptested: "groupTested",
        "specific test performed": "typeOfTest",
        "type of test": "typeOfTest",
        typeoftest: "typeOfTest",
        "test method / equipment used": "scopeStandard",
        "scope standard": "scopeStandard",
        scopestandard: "scopeStandard",
        "range type": "rangeType",
        rangetype: "rangeType",
        "range ll": "rangeLL",
        rangell: "rangeLL",
        //"range ll unit": "rangeLLUnit",
        rangellunit: "rangeLLUnit",
        "range ul": "rangeUL",
        rangeul: "rangeUL",
        //"range ul unit": "rangeULUnit",
        //rangeulunit: "rangeULUnit",
        "unit of measurement": "rangeULUnit",
        rangeulunit: "rangeULUnit",
        mu: "mu",
        //cv: "cv",
        "@Observed Value": "cv",
        cv: "cv",
        "facility type": "testPerformedAt",
        "test performed at": "testPerformedAt",
        testperformedat: "testPerformedAt",
        "pt available": "ptAvailable",
        ptavailable: "ptAvailable",
        "range remarks": "rangeRemark",
        rangeremarks: "rangeRemark",
      };

      // Process each row of data
      const processedScopes = [];

      dataRows.forEach((row, index) => {
        const scopeObj = {};

        // Map each cell to the appropriate field
        headers.forEach((header, cellIndex) => {
          const normalizedHeader = header.toLowerCase().trim();
          const mappedField = columnMapping[normalizedHeader];
          const cellValue = row[cellIndex]?.toString().trim() || "";

          if (mappedField && cellValue) {
            scopeObj[mappedField] = cellValue;
          }
        });

        // Include rows that have either:
        // 1. Test code and required fields (for normal operations)
        // 2. ID and Group Tested (for update operations without test code)
        const hasTestCodeAndRequired = scopeObj.testCode && scopeObj.groupTested && scopeObj.typeOfTest;
        const hasIdAndGroupTested = scopeObj.id && scopeObj.groupTested;

        if (hasTestCodeAndRequired || hasIdAndGroupTested) {
          // Convert numeric fields
          if (scopeObj.rangeLL)
            scopeObj.rangeLL = parseFloat(scopeObj.rangeLL) || null;
          if (scopeObj.rangeUL)
            scopeObj.rangeUL = parseFloat(scopeObj.rangeUL) || null;
          if (scopeObj.mu) scopeObj.mu = parseFloat(scopeObj.mu) || null;

          // Set defaults
          scopeObj.rangeType = scopeObj.rangeType || "Quantitative";
          scopeObj.testPerformedAt =
            scopeObj.testPerformedAt || "PermanentSiteFacility";

          processedScopes.push(scopeObj);
        }
      });

      if (processedScopes.length === 0) {
        showNotification(
          "No valid rows found. Please ensure you have either: (1) Test Code, Group Tested, and Type of Test filled, OR (2) ID and Group Tested filled for updates.",
          "warning"
        );
        return;
      }

      // Process scope data - update existing only (skip new entries)
      let updatedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;
      const skippedScopes = [];

      for (const scope of processedScopes) {
        try {
          // Check if scope exists by ID first (if provided), then by Test Code
          let existingScope = null;

          console.log(
            `Processing scope - ID: ${scope.id}, TestCode: ${scope.testCode}, GroupTested: ${scope.groupTested}`
          );

          if (scope.id && scope.id.toString().trim() !== "") {
            if (scope.testCode) {
              // Match by both ID and Test Code for precision
              existingScope = existingScopes.find(
                (existing) =>
                  existing.Id.toString() === scope.id.toString() &&
                  existing.TestCode === scope.testCode
              );
              console.log(`ID+TestCode match found: ${!!existingScope}`);
            } else if (scope.groupTested) {
              // Match by ID and Group Tested when Test Code is not available
              existingScope = existingScopes.find(
                (existing) =>
                  existing.Id.toString() === scope.id.toString() &&
                  existing.GroupTested === scope.groupTested
              );
              console.log(`ID+GroupTested match found: ${!!existingScope}`);
            } else {
              // Match by ID only as last resort
              existingScope = existingScopes.find(
                (existing) => existing.Id.toString() === scope.id.toString()
              );
              console.log(`ID only match found: ${!!existingScope}`);
            }
          }

          // If no ID match found, fallback to Test Code only (for backward compatibility)
          if (!existingScope && scope.testCode) {
            existingScope = existingScopes.find(
              (existing) => existing.TestCode === scope.testCode
            );
            console.log(`TestCode only match found: ${!!existingScope}`);
          }

          if (existingScope) {
            // Update existing scope only
            const scopePayload = {
              rangeRemark: scope.rangeRemark || "",
              rangeType: scope.rangeType || "Quantitative",
              RangeLL: scope.rangeLL || null,
              RangeLLUnit: scope.rangeLLUnit || "",
              RangeUL: scope.rangeUL || null,
              RangeULUnit: scope.rangeULUnit || "",
              mu: scope.mu || null,
              cv: scope.cv || "",
              testPerformedAt: scope.testPerformedAt || "",
            };

            await TestingLaboratoryService.updateExistingScope(
              existingScope.Id,
              scopePayload.rangeRemark,
              scopePayload.rangeType,
              scopePayload.RangeLL,
              scopePayload.RangeLLUnit,
              scopePayload.RangeUL,
              scopePayload.RangeULUnit,
              scopePayload.mu,
              scopePayload.cv,
              "", // mv - empty (not using cvmu)
              scopePayload.testPerformedAt,
              "", // EquipmentName - empty
              "", // Ref_Id - empty
              "" // Pt_id - empty
            );
            updatedCount++;
          } else {
            // Skip scope (not found in existing data)
            skippedCount++;
            skippedScopes.push({
              id: scope.id || "No ID",
              testCode: scope.testCode,
              typeOfTest: scope.typeOfTest || "Unknown",
            });
          }
        } catch (scopeError) {
          console.error(
            `Error processing scope ${scope.testCode}:`,
            scopeError
          );
          errorCount++;
        }
      }

      // Show results with detailed information
      const resultMessages = [];
      if (updatedCount > 0)
        resultMessages.push(`${updatedCount} scope(s) updated`);
      if (skippedCount > 0)
        resultMessages.push(`${skippedCount} new scope(s) skipped`);
      if (errorCount > 0)
        resultMessages.push(`${errorCount} error(s) occurred`);

      showNotification(
        `Bulk Update completed! ${resultMessages.join(", ")}.`,
        errorCount > 0 ? "warning" : skippedCount > 0 ? "info" : "success"
      );

      // Close preview modal and reset states
      handleClosePreviewModal();

      // Refresh existing scopes
      await loadExistingScopes();
    } catch (error) {
      console.error("Error during bulk Update:", error);
      showNotification("Failed to update data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get adequacy status for a scope item
  const getScopeStatus = (scopeId) => {
    const dancRecord = dancRecords.find(
      (record) =>
        record.sourceid && parseInt(record.sourceid) === parseInt(scopeId)
    );
    return dancRecord ? dancRecord.InformationStatus : "Not Assessed";
  };

  // Check if selection conflicts with business rules
  const isValidScopeSelection = (currentSelection, newScopeId, isAdding) => {
    if (!isAdding) return true; // Always allow deselection

    const newScopeStatus = getScopeStatus(newScopeId);
    const currentStatuses = currentSelection.map((id) => getScopeStatus(id));

    // Check if current selection has Adequate or Inadequate
    const hasAdequate = currentStatuses.includes("Adequate");
    const hasInadequate = currentStatuses.includes("Inadequate");

    // Rule 1: Cannot select both Adequate and Inadequate
    if (newScopeStatus === "Adequate" && hasInadequate) {
      showNotification(
        "Cannot select both Adequate and Inadequate scopes at the same time",
        "error"
      );
      return false;
    }
    if (newScopeStatus === "Inadequate" && hasAdequate) {
      showNotification(
        "Cannot select both Adequate and Inadequate scopes at the same time",
        "error"
      );
      return false;
    }

    // Rule 2: Can select Adequate/Inadequate + Not Assessed (this is allowed)
    return true;
  };

  // Load DA NC data for all sections

  // Handle Excel export of existing scope data
  const handleExportExistingData = async () => {
    try {
      let page = 1;
      const pageSize = 100;
      let allData = [];
      let hasNextPage = true;

      while (hasNextPage) {
        const result = await TestingLaboratoryService.getExistingScopes(
          formData.appId,
          formData.orgId,
          formData.disciplineId,
          formData.groupId,
          page,
          pageSize,
          "",
        );

        if (result.success) {
          allData = [...allData, ...(result.data || [])];

          hasNextPage = result.pagination?.hasNextPage || false;
          page++;
        } else {
          hasNextPage = false;
        }
      }
      if (!allData || allData.length === 0) {
        showNotification("No data available to export", "warning");
        return;
      }

      // Prepare data for Excel export
      const exportData = allData.map((scope, index) => ({
        ID: scope.Id || "",
        "Test Code": scope.TestCode || "",
        "Discipline Name": formData.disciplineName || "",
        "Materials/Products Tested": scope.GroupTested || "",
        "Specific Test Performed": scope.TypeOfTest || "",
        "Test Method / Equipment Used": scope.ScopeStandard || "",
        "Facility Type": scope.testPerformedAt || "",
        "Range Type": scope.rangeType || "",
        "Range Remarks": scope.rangeRemark || "",
        "Range LL": scope.RangeLL || "",
        //"Range LL Unit": scope.RangeLLUnit || "",
        "Range UL": scope.RangeUL || "",
        //"Range UL Unit": scope.RangeULUnit || "",
        "Unit of measurement": scope.RangeULUnit || "",
        Mu: scope.mu || "",
        //CV: scope.cv || scope.CV || "",
        "@Observed Value": scope.cv || scope.CV || "",
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, i) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => String(row[key] || "").length)
        );
        colWidths[i] = { wch: Math.min(maxLength + 2, 30) };
      });
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Existing Scopes");

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `Existing_Scopes_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      showNotification(`Excel file downloaded: ${filename}`, "success");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      showNotification("Failed to export data to Excel", "error");
    }
  };

  // Handle Add Other Scope
  const handleAddOtherScope = () => {
    setAddOtherModalOpen(true);
  };

  const handleCloseOtherModal = () => {
    setAddOtherModalOpen(false);
    // Reset form
    setOtherScopeForm({
      groupTested: "",
      typeOfTest: "",
      scopeStandard: "",
      rangeType: "Quantitative",
      rangeLL: "",
      rangeLLUnit: "",
      rangeUL: "",
      rangeULUnit: "",
      qualitativeRemarks: "",
      mu: "",
      cv: "",
      testPerformedAt: "PermanentSiteFacility",
    });
  };

  const handleOtherFormChange = (field, value) => {
    setOtherScopeForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitOtherScope = async () => {
    try {
      setOtherScopeLoading(true);

      // Get values from localStorage and URL params
      const appId = localStorage.getItem("appId");
      const orgId = localStorage.getItem("orgId");
      // Use decrypted disciplineId from formData instead of raw encrypted one from URL
      const disciplineId = formData.disciplineId;
      const groupId = formData.groupId;

      // Debug: Validate decrypted parameters
      console.log('📝 Scope Other Save - Parameters Check:', {
        appId,
        orgId,
        disciplineId,
        disciplineName: formData.disciplineName
      });

      // Validate required fields
      const baseRequiredFields = ["groupTested", "typeOfTest"];
      let missingFields = baseRequiredFields.filter(
        (field) => !otherScopeForm[field]
      );

      // Add conditional required fields based on range type
      if (otherScopeForm.rangeType === "Quantitative") {
        const quantitativeFields = [
          "rangeLL",
          "rangeLLUnit",
          "rangeUL",
          "rangeULUnit",
        ];
        const missingQuantitativeFields = quantitativeFields.filter(
          (field) => !otherScopeForm[field]
        );
        missingFields = [...missingFields, ...missingQuantitativeFields];
      } else if (
        otherScopeForm.rangeType === "Qualitative" ||
        otherScopeForm.rangeType === "Calculative"
      ) {
        if (!otherScopeForm.qualitativeRemarks) {
          missingFields.push("qualitativeRemarks");
        }
      }

      if (missingFields.length > 0) {
        showNotification(
          `Please fill all required fields: ${missingFields.join(", ")}`,
          "warning"
        );
        return;
      }

      if (!appId || !orgId || !disciplineId || !groupId) {
        console.error('❌ Scope Other Save - Missing required parameters:', { appId, orgId, disciplineId });
        showNotification(
          "Missing application parameters. Please navigate back from discipline section.",
          "error"
        );
        return;
      }

      // Prepare scope data
      const scopeData = {
        appId: parseInt(appId),
        orgId: parseInt(orgId),
        disciplineId: parseInt(disciplineId),
        groupId: groupId,
        testCode: "", // Set test code as blank for other scopes
        groupTested: otherScopeForm.groupTested,
        typeOfTest: otherScopeForm.typeOfTest,
        scopeStandard: otherScopeForm.scopeStandard,
        rangeType: otherScopeForm.rangeType,
        rangeLL:
          otherScopeForm.rangeType === "Quantitative" && otherScopeForm.rangeLL
            ? parseFloat(otherScopeForm.rangeLL)
            : null,
        rangeLLUnit:
          otherScopeForm.rangeType === "Quantitative"
            ? otherScopeForm.rangeLLUnit
            : "",
        rangeUL:
          otherScopeForm.rangeType === "Quantitative" && otherScopeForm.rangeUL
            ? parseFloat(otherScopeForm.rangeUL)
            : null,
        rangeULUnit:
          otherScopeForm.rangeType === "Quantitative"
            ? otherScopeForm.rangeULUnit
            : "",
        mu: otherScopeForm.mu ? parseFloat(otherScopeForm.mu) : null,
        cv: otherScopeForm.cv,
        testPerformedAt: otherScopeForm.testPerformedAt,
      };

      // Save scope to database
      await TestingLaboratoryService.saveOtherScope(scopeData);

      showNotification("Scope added successfully!", "success");
      handleCloseOtherModal();

      // Refresh existing scopes list
      if (typeof loadExistingScopes === "function") {
        loadExistingScopes();
      }
    } catch (error) {
      console.error("Error adding scope:", error);
      showNotification("Error adding scope", "error");
    } finally {
      setOtherScopeLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Paper
        sx={{
          mb: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 1,
            mb: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🔬 Scope Management
            </Typography>
            {isScopeSectionUnfrozen && (
              <Chip
                label="UNFROZEN"
                size="small"
                sx={{
                  backgroundColor: "#ff9800",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  animation: "pulse 2s infinite",
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Display current discipline info */}
          {formData.disciplineName && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Current Discipline/Group: {formData.disciplineName}
                {" - "}
                {formData.groupName}
              </Typography>
            </Alert>
          )}

          {/* Unfreeze Alert */}
          {isScopeSectionUnfrozen && (
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                "& .MuiAlert-icon": { alignItems: "center" },
                backgroundColor: "#fff3cd",
                borderColor: "#ffeaa7",
                color: "#856404",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                🔓 This Scope Section has been unfrozen and is temporarily
                editable.
              </Typography>
            </Alert>
          )}

          {/* Tabs for different sections */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 3,
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "1rem",
              },
            }}
          >
            <Tab label="� Instructions" />
            {(applicationStage === null ||
              applicationStage === "" ||
              applicationStage === "18a" ||
              applicationStage === "18" ||
              applicationStage === "18b" ||
              isScopeSectionUnfrozen) && <Tab label="📝 Add Scope" />}
            {(applicationStage === null ||
              applicationStage === "" ||
              applicationStage === "18a" ||
              applicationStage === "18" ||
              applicationStage === "18b" ||
              isScopeSectionUnfrozen) && <Tab label="📤 Bulk Update" />}
            <Tab label="📋 Existing Scopes" />
          </Tabs>

          {/* Tab 0: Instructions */}
          {activeTab === 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, color: "#1976d2", fontWeight: 600 }}
                >
                  📋 Scope — Step-by-step Instructions
                </Typography>

                {instructionsLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading instructions...
                    </Typography>
                  </Box>
                ) : instructions.length > 0 ? (
                  <Box
                    sx={{
                      pl: 2,
                      "& p": {
                        mb: 1,
                        fontSize: "0.95rem",
                        lineHeight: 1.6,
                        color: "#424242",
                      },
                    }}
                  >
                    {instructions.map((instruction, index) => (
                      <Typography key={instruction.id} component="p">
                        {index + 1}. {instruction.instruction}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      "& .section": { mb: 3 },
                      "& .section-title": {
                        fontWeight: 600,
                        color: "#334155",
                        mb: 1,
                        fontSize: "1.1rem",
                      },
                      "& .step-list": {
                        pl: 3,
                        "& li": {
                          mb: 1,
                          fontSize: "0.95rem",
                          lineHeight: 1.6,
                          color: "#424242",
                        },
                      },
                    }}
                  >
                    <Box className="section">
                      <Typography className="section-title">
                        A. Select Scope items from the Model list
                      </Typography>
                      <ol className="step-list">
                        <li>Go to Scope.</li>
                        <li>
                          In Model Equipment & Scope, search and select the
                          relevant Scope items (aligned with your Discipline and
                          Equipment).
                        </li>
                        <li>
                          The form will prefill the standard scope fields (as
                          per model).
                        </li>
                        <li>
                          Enter any remaining values required on the form.
                        </li>
                        <li>Save.</li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        B. Map Scope to your Equipment
                      </Typography>
                      <ol className="step-list">
                        <li>
                          Still on the Scope page, map each selected Scope item
                          to the correct Equipment you added earlier.
                        </li>
                        <li>
                          Verify the mapping (the same Equipment can map to
                          multiple Scope items if applicable).
                        </li>
                        <li>Save.</li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        C. Add or update Scope using Bulk Update
                      </Typography>
                      <ol className="step-list">
                        <li>
                          In Bulk Update, click Download Excel to get the
                          pre-filled template (based on your chosen Scope
                          models).
                        </li>
                        <li>
                          Fill in only the remaining/blank columns as guided by
                          the template.
                        </li>
                        <li>Upload the completed file.</li>
                        <li>
                          Check the validation report; correct and re-upload if
                          needed.
                        </li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        D. Edit or review existing Scope
                      </Typography>
                      <ol className="step-list">
                        <li>Open View Existing.</li>
                        <li>Search/filter to find the Scope line.</li>
                        <li>Click Edit, adjust values, and Save.</li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        E. When the Scope item isn't in the Model list
                      </Typography>
                      <ol className="step-list">
                        <li>
                          Go to Other → Add Scope and submit it one-by-one.
                        </li>
                        <li>
                          Once saved, you can map it to the relevant Equipment
                          on the Scope page.
                        </li>
                      </ol>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tab 1: Add Scope */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isScopeSectionUnfrozen) &&
            activeTab === 1 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, color: "#334155" }}>
                        Add New Scope Entries
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select scopes from master data or add custom entries
                      </Typography>
                    </Box>
                    {/* <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AddIcon />}
                      onClick={handleAddOtherScope}
                      size="small"
                    >
                      Add Other Scope
                    </Button>
                  </Box> */}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Search and Filter Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search scopes..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      sx={{ flex: 1, maxWidth: 300 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Sub Group</InputLabel>
                      <Select
                        value={selectedSubGroupMaster}
                        onChange={(e) => handleSearchSubGroup(e.target.value)}
                        label="Sub Group"
                        disabled={subGroupsLoading}
                      >
                        <MenuItem value="">All</MenuItem>
                        {subGroups.map((group) => (
                          <MenuItem key={group.sGrpId} value={group.sGrpId}>
                            {group.subGrpName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Per Page</InputLabel>
                      <Select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(e.target.value);
                          setCurrentPage(1);
                          loadScopeMasterData(1, searchQuery);
                        }}
                        label="Per Page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        loadScopeMasterData(currentPage, searchQuery)
                      }
                      disabled={scopeMasterLoading}
                    >
                      {scopeMasterLoading ? "Loading..." : "Load Scopes"}
                    </Button>
                  </Box>

                  {/* Scope Master Data Table */}
                  {scopeMasterLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {scopeMasterData.length} of {totalRecords}{" "}
                          results
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {selectedScopes.length} selected
                        </Typography>
                      </Box>

                      <TableContainer
                        component={Paper}
                        sx={{ maxHeight: 600, border: "1px solid #e2e8f0" }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={
                                    scopeMasterData.length > 0 &&
                                    scopeMasterData.every((scope) =>
                                      selectedScopes.includes(scope.Id),
                                    )
                                  }
                                  indeterminate={
                                    scopeMasterData.some((scope) =>
                                      selectedScopes.includes(scope.Id),
                                    ) &&
                                    !scopeMasterData.every((scope) =>
                                      selectedScopes.includes(scope.Id),
                                    )
                                  }
                                  onChange={(e) =>
                                    handleSelectAll(e.target.checked)
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Test Code
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Sub Group
                              </TableCell>
                              {/* <TableCell sx={{ fontWeight: 600 }}>Discipline Name</TableCell> */}
                              <TableCell sx={{ fontWeight: 600 }}>
                                Materials or Products tested
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Component, parameter or characteristic tested
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Test Method Specification
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Qualitative/Quantitative
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                PT Availability in Market
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {scopeMasterData.map((scope) => (
                              <TableRow
                                key={scope.Id}
                                sx={{
                                  "&:hover": { backgroundColor: "#f8fafc" },
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedScopes.includes(scope.Id)}
                                    onChange={(e) =>
                                      handleScopeSelection(
                                        scope.Id,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>{scope.TestCode}</TableCell>
                                <TableCell>{scope.SubGroupName}</TableCell>
                                <TableCell>{scope.GroupTested}</TableCell>
                                <TableCell>{scope.TypeOfTest}</TableCell>
                                <TableCell>{scope.ScopeStandard}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={scope.Category}
                                    size="small"
                                    color={
                                      scope.Category === "Quantitative"
                                        ? "primary"
                                        : "secondary"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={scope.PTAvailable}
                                    size="small"
                                    color={
                                      scope.PTAvailable === "Yes"
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                            {scopeMasterData.length === 0 &&
                              !scopeMasterLoading && (
                                <TableRow>
                                  <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 4 }}
                                  >
                                    <Typography color="text.secondary">
                                      {searchQuery
                                        ? "No scope data found for your search"
                                        : 'Click "Load Scopes" to view available scope entries'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mt: 2,
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            {Array.from(
                              { length: Math.min(5, totalPages) },
                              (_, i) => {
                                const pageNum =
                                  Math.max(1, currentPage - 2) + i;
                                if (pageNum > totalPages) return null;

                                return (
                                  <Button
                                    key={pageNum}
                                    size="small"
                                    variant={
                                      pageNum === currentPage
                                        ? "contained"
                                        : "outlined"
                                    }
                                    onClick={() => handlePageChange(pageNum)}
                                    sx={{ minWidth: 40 }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              },
                            )}
                          </Box>

                          <Button
                            size="small"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>

                          <Typography variant="body2" sx={{ ml: 2 }}>
                            Page {currentPage} of {totalPages}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      {selectedScopes.length > 0 &&
                        (applicationStage === null ||
                          applicationStage === "" ||
                          applicationStage === "18a") && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 3,
                              gap: 2,
                            }}
                          >
                            <Button
                              onClick={() => {
                                setSelectedScopes([]);
                                setSelectedScopeData([]);
                              }}
                              variant="outlined"
                            >
                              Clear Selection
                            </Button>
                            <Button
                              onClick={handleAddSelectedScopes}
                              variant="contained"
                              startIcon={<SaveIcon />}
                              disabled={selectedScopes.length === 0}
                            >
                              Add Selected ({selectedScopes.length})
                            </Button>
                          </Box>
                        )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

          {/* Tab 2: Bulk Update */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isScopeSectionUnfrozen) &&
            activeTab === 2 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "#334155" }}>
                    Bulk Update Scopes
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Upload an Excel file to preview and update your existing
                    scope data using Bulk Update.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <input
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        id="bulk-upload-input"
                        type="file"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="bulk-upload-input">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          fullWidth
                          sx={{ py: 1.5 }}
                        >
                          Select File & Preview
                        </Button>
                      </label>
                      {uploadFile && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: "green" }}
                        >
                          Selected: {uploadFile.name}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportExistingData}
                          disabled={
                            !existingScopes || existingScopes.length === 0
                          }
                        >
                          Export Existing Data
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Instructions */}
                  <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Bulk Update Instructions:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • <strong>Select File & Preview:</strong> Choose an Excel
                      file to preview data before updating
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • <strong>Review & Update:</strong> The preview will show
                      validation status and allow you to confirm updates
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • Use "Export Existing Data" to download your current
                      scopes, make changes, and upload back
                    </Typography>
                    <Typography variant="body2">
                      • Scopes can be updated by matching: (1) Test Code, or (2)
                      ID + Group Tested combination. New entries will be
                      skipped.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}

          {/* Tab 3: Existing Scopes (index changes based on stage) */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            applicationStage === "20") &&
            activeTab === 3 && (
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: "#334155" }}>
                        Existing Scopes ({existingScopes.length})
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {selectedExistingScopes.length > 0 && (
                        <>
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleMultipleDelete}
                            size="small"
                            sx={{
                              animation: "fadeIn 0.3s ease-in-out",
                              "@keyframes fadeIn": {
                                from: {
                                  opacity: 0,
                                  transform: "translateY(-10px)",
                                },
                                to: { opacity: 1, transform: "translateY(0)" },
                              },
                            }}
                          >
                            Delete Selected ({selectedExistingScopes.length})
                          </Button>
                          <Button
                            variant="contained"
                            color="info"
                            startIcon={<AddIcon />}
                            onClick={handleOpenBindingModal}
                            size="small"
                            sx={{
                              animation: "fadeIn 0.3s ease-in-out",
                              "@keyframes fadeIn": {
                                from: {
                                  opacity: 0,
                                  transform: "translateY(-10px)",
                                },
                                to: { opacity: 1, transform: "translateY(0)" },
                              },
                            }}
                          >
                            Bind Equipment, Reference Material & PT (
                            {selectedExistingScopes.length})
                          </Button>
                        </>
                      )}

                      {/* Bulk Adequacy Check Button - Show when scopes are selected */}
                      {selectedAdequacyScopes.length > 0 && (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleOpenBulkAdequacyModal}
                          size="small"
                          sx={{
                            animation: "fadeIn 0.3s ease-in-out",
                            backgroundColor: "#28a745",
                            "&:hover": {
                              backgroundColor: "#218838",
                            },
                            "@keyframes fadeIn": {
                              from: {
                                opacity: 0,
                                transform: "translateY(-10px)",
                              },
                              to: { opacity: 1, transform: "translateY(0)" },
                            },
                          }}
                        >
                          Mark Adequacy ({selectedAdequacyScopes.length})
                        </Button>
                      )}

                      {/* Test Witness Button - Show when scopes are selected and userTypeId is 6 */}
                      {selectedTestWitnessScopes.length > 0 &&
                        localStorage.getItem("userTypeId") === "6" && (
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<CheckCircleIcon />}
                            onClick={handleOpenTestWitnessModal}
                            size="small"
                            sx={{
                              animation: "fadeIn 0.3s ease-in-out",
                              backgroundColor: "#ff9800",
                              "&:hover": {
                                backgroundColor: "#f57c00",
                              },
                              "@keyframes fadeIn": {
                                from: {
                                  opacity: 0,
                                  transform: "translateY(-10px)",
                                },
                                to: { opacity: 1, transform: "translateY(0)" },
                              },
                            }}
                          >
                            Mark for Test Witness (
                            {selectedTestWitnessScopes.length})
                          </Button>
                        )}

                      {/* Add Scope buttons - Only show when stage is null */}
                      {(applicationStage === null ||
                        applicationStage === "" ||
                        applicationStage === "18a" ||
                        isScopeSectionUnfrozen) && (
                        <>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddScope}
                            size="small"
                          >
                            Add Scope
                          </Button>
                          {/* <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={handleAddOtherScope}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Add Other Scope
                        </Button> */}
                        </>
                      )}
                    </Box>
                  </Box>

                  {existingScopesLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Search and Pagination Controls */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mb: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="Search scopes..."
                          value={existingScopesSearchQuery}
                          onChange={(e) =>
                            setExistingScopesSearchQuery(e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              setExistingScopesPage(1);
                              loadExistingScopes(
                                1,
                                existingScopesPageSize,
                                existingScopesSearchQuery,
                              );
                            }
                          }}
                          sx={{ flex: 1, maxWidth: 300 }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setExistingScopesPage(1);
                            loadExistingScopes(
                              1,
                              existingScopesPageSize,
                              existingScopesSearchQuery,
                            );
                          }}
                          size="small"
                        >
                          Search
                        </Button>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Sub Group</InputLabel>
                          <Select
                            value={selectedSubGroup}
                            onChange={(e) => {
                              setSelectedSubGroup(e.target.value);
                              setExistingScopesPage(1);
                              loadExistingScopes(
                                1,
                                existingScopesPageSize,
                                e.target.value,
                              );
                            }}
                            label="Sub Group"
                            disabled={subGroupsLoading}
                          >
                            <MenuItem value="">All</MenuItem>
                            {subGroups.map((group) => (
                              <MenuItem key={group.sGrpId} value={group.sGrpId}>
                                {group.subGrpName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Per Page</InputLabel>
                          <Select
                            value={existingScopesPageSize}
                            onChange={(e) => {
                              const newPageSize = e.target.value;
                              setExistingScopesPageSize(newPageSize);
                              setExistingScopesPage(1);
                              loadExistingScopes(
                                1,
                                newPageSize,
                                existingScopesSearchQuery,
                              );
                            }}
                            label="Per Page"
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Filter Controls */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mb: 2,
                          alignItems: "center",
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>PT Availability</InputLabel>
                          <Select
                            value={ptAvailabilityFilter}
                            onChange={(e) =>
                              setPtAvailabilityFilter(e.target.value)
                            }
                            label="PT Availability"
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Qualitative/Quantitative</InputLabel>
                          <Select
                            value={qualitativeQuantitativeFilter}
                            onChange={(e) =>
                              setQualitativeQuantitativeFilter(e.target.value)
                            }
                            label="Qualitative/Quantitative"
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Qualitative">Qualitative</MenuItem>
                            <MenuItem value="Quantitative">
                              Quantitative
                            </MenuItem>
                            <MenuItem value="Calculative">Calculative</MenuItem>
                            <MenuItem value="Others">Others</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setPtAvailabilityFilter("");
                            setQualitativeQuantitativeFilter("");
                            setSearchQuery("");
                            setSelectedSubGroup("");
                            setExistingScopesPage(1);
                            loadExistingScopes(1, existingScopesPageSize, "");
                          }}
                        >
                          Clear Filters
                        </Button>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Please Check the Checkbox and bind the Equipment,
                        ReferenceMaterial and PT by click on "Bind Equipment,
                        Reference Material & PT"
                      </Alert>

                      {/* Pagination Info */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {existingScopes.length} of{" "}
                          {existingScopesTotalRecords} results
                        </Typography>
                        <Typography variant="body2" color="primary">
                          Page {existingScopesPage} of{" "}
                          {existingScopesTotalPages}
                        </Typography>
                      </Box>
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: 600,
                          boxShadow: "none",
                          border: "1px solid #e2e8f0",
                          overflowX: "auto",
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                              {/* Delete/Bind checkbox - Only show when stage is null */}
                              {(applicationStage === null ||
                                applicationStage === "" ||
                                applicationStage === "18a") && (
                                <TableCell
                                  padding="checkbox"
                                  sx={{ minWidth: 80 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Checkbox
                                      checked={
                                        selectedExistingScopes.length ===
                                          getFilteredExistingScopes().length &&
                                        getFilteredExistingScopes().length > 0
                                      }
                                      indeterminate={
                                        selectedExistingScopes.length > 0 &&
                                        selectedExistingScopes.length <
                                          getFilteredExistingScopes().length
                                      }
                                      onChange={(e) =>
                                        handleSelectAllForDelete(
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      S No.
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.65rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      Delete/Bind
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )}

                              {/* Adequacy checkbox - Show only when stage is 18/18b/20 AND userTypeId is 4 or 6 */}
                              {(applicationStage === "18" ||
                                applicationStage === "18b" ||
                                applicationStage === "20") &&
                                (localStorage.getItem("userTypeId") === "4" ||
                                  localStorage.getItem("userTypeId") ===
                                    "6") && (
                                  <TableCell
                                    padding="checkbox"
                                    sx={{ minWidth: 80 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Checkbox
                                        checked={
                                          selectedAdequacyScopes.length ===
                                            getFilteredExistingScopes()
                                              .length &&
                                          getFilteredExistingScopes().length > 0
                                        }
                                        indeterminate={
                                          selectedAdequacyScopes.length > 0 &&
                                          selectedAdequacyScopes.length <
                                            getFilteredExistingScopes().length
                                        }
                                        onChange={(e) =>
                                          handleSelectAllAdequacyScopes(
                                            e.target.checked,
                                          )
                                        }
                                        sx={{ color: "#28a745" }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          fontSize: "0.7rem",
                                          fontWeight: 600,
                                          color: "#28a745",
                                        }}
                                      >
                                        S No.
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          fontSize: "0.65rem",
                                          color: "#28a745",
                                        }}
                                      >
                                        Adequacy
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                )}

                              {/* Test Witness checkbox - Show only when userTypeId is 6 */}
                              {localStorage.getItem("userTypeId") === "6" && (
                                <TableCell
                                  padding="checkbox"
                                  sx={{ minWidth: 80 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Checkbox
                                      checked={(() => {
                                        const availableScopes =
                                          getFilteredExistingScopes().filter(
                                            (scope) =>
                                              scope.Istestwitness !== 1,
                                          );
                                        return (
                                          selectedTestWitnessScopes.length ===
                                            availableScopes.length &&
                                          availableScopes.length > 0
                                        );
                                      })()}
                                      indeterminate={(() => {
                                        const availableScopes =
                                          getFilteredExistingScopes().filter(
                                            (scope) =>
                                              scope.Istestwitness !== 1,
                                          );
                                        return (
                                          selectedTestWitnessScopes.length >
                                            0 &&
                                          selectedTestWitnessScopes.length <
                                            availableScopes.length
                                        );
                                      })()}
                                      onChange={(e) =>
                                        handleSelectAllTestWitnessScopes(
                                          e.target.checked,
                                        )
                                      }
                                      sx={{ color: "#ff9800" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        color: "#ff9800",
                                      }}
                                    >
                                      S No.
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.65rem",
                                        color: "#ff9800",
                                      }}
                                    >
                                      Test Witness
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )}

                              {(applicationStage === "18" ||
                                applicationStage === "18a" ||
                                applicationStage === "18b" ||
                                applicationStage === "20") && (
                                <TableCell
                                  sx={{ fontWeight: 600, minWidth: 120 }}
                                  align="center"
                                >
                                  Adequacy Status
                                </TableCell>
                              )}
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 100 }}
                              >
                                Sub Group
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 300 }}
                              >
                                Scope Details <br />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#1976d2",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Test Code ,
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#00897b",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Materials/Products Tested ,
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#e65100",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Specific Test Performed ,
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#c2185b",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Test Method / Equipment Used
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 280 }}
                              >
                                Range Details
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 200 }}
                              >
                                Equipment
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 240 }}
                              >
                                Reference Material
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 240 }}
                              >
                                Proficiency Testing
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, minWidth: 100 }}
                              >
                                PT Available in Market
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getFilteredExistingScopes().map((scope, index) => {
                              // ✅ STEP 1: SPLIT VALUES
                              const facilities = scope.testPerformedAt
                                ? scope.testPerformedAt
                                    .split(",")
                                    .map((f) => f.trim())
                                : [];

                              // ✅ STEP 2: MAP VALUES
                              const facilityMap = {
                                MobileFacility: "POCT (Point of Care Testing)",
                                TemporaryFacility: "Temporary Facility",
                                PermanentFacility: "Permanent Facility",
                              };

                              const formattedFacilities = facilities.map(
                                (f) => facilityMap[f] || f,
                              );
                              return (
                                <TableRow
                                  key={scope.Id}
                                  sx={{
                                    "&:hover": { backgroundColor: "#f8fafc" },
                                  }}
                                >
                                  {/* Delete/Bind checkbox - Only show when stage is null */}
                                  {(applicationStage === null ||
                                    applicationStage === "" ||
                                    applicationStage === "18a" ||
                                    isScopeSectionUnfrozen) && (
                                    <TableCell padding="checkbox">
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Checkbox
                                          checked={selectedExistingScopes.includes(
                                            scope.Id,
                                          )}
                                          onChange={(e) =>
                                            handleSelectForDelete(
                                              scope.Id,
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            color: "#1976d2",
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.5,
                                            mt: 0.5,
                                            justifyContent: "center",
                                          }}
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEditScope(scope);
                                            }}
                                            sx={{ color: "#1976d2", p: 0.5 }}
                                            title="Edit"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleDeleteScope(scope.Id);
                                            }}
                                            sx={{ color: "#d32f2f", p: 0.5 }}
                                            title="Delete"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                  )}

                                  {/* Adequacy checkbox - Show only when stage is 18/18b/20 AND userTypeId is 4 or 6 */}
                                  {(applicationStage === "18" ||
                                    applicationStage === "18b" ||
                                    applicationStage === "20") &&
                                    (localStorage.getItem("userTypeId") ===
                                      "4" ||
                                      localStorage.getItem("userTypeId") ===
                                        "6") && (
                                      <TableCell padding="checkbox">
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Checkbox
                                            checked={selectedAdequacyScopes.includes(
                                              scope.Id,
                                            )}
                                            onChange={(e) =>
                                              handleAdequacyScopeSelection(
                                                scope.Id,
                                                e.target.checked,
                                              )
                                            }
                                            sx={{
                                              color:
                                                adequacyChecks[scope.Id]
                                                  ?.status === "Adequate"
                                                  ? "#28a745"
                                                  : "#007bff",
                                              "&.Mui-checked": {
                                                color:
                                                  adequacyChecks[scope.Id]
                                                    ?.status === "Adequate"
                                                    ? "#28a745"
                                                    : "#007bff",
                                              },
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                              color: "#28a745",
                                            }}
                                          >
                                            {index + 1}
                                          </Typography>
                                          {adequacyChecks[scope.Id]
                                            ?.isSubmitted && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mt: 0.5,
                                              }}
                                            >
                                              <CheckCircleIcon
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  color:
                                                    adequacyChecks[scope.Id]
                                                      ?.status === "Adequate"
                                                      ? "#28a745"
                                                      : "#ffc107",
                                                }}
                                              />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "0.6rem",
                                                  color:
                                                    adequacyChecks[scope.Id]
                                                      ?.status === "Adequate"
                                                      ? "#28a745"
                                                      : "#ffc107",
                                                }}
                                              >
                                                {
                                                  adequacyChecks[scope.Id]
                                                    ?.status
                                                }
                                              </Typography>
                                              {/* Clause Number Chip for inadequate status with clause number */}
                                              {adequacyChecks[scope.Id]
                                                ?.clauseNo &&
                                                adequacyChecks[scope.Id]
                                                  ?.status === "Inadequate" && (
                                                  <Chip
                                                    label={
                                                      adequacyChecks[scope.Id]
                                                        .clauseNo
                                                    }
                                                    size="small"
                                                    sx={{
                                                      ml: 0.5,
                                                      mt: 0.25,
                                                      height: "16px",
                                                      fontSize: "0.55rem",
                                                      backgroundColor:
                                                        "#ffebee",
                                                      color: "#d32f2f",
                                                      border:
                                                        "1px solid #ffcdd2",
                                                      "& .MuiChip-label": {
                                                        px: 0.5,
                                                      },
                                                    }}
                                                  />
                                                )}
                                            </Box>
                                          )}
                                        </Box>
                                      </TableCell>
                                    )}
                                  {/* Test Witness checkbox - Show only when userTypeId is 6 */}
                                  {localStorage.getItem("userTypeId") ===
                                    "6" && (
                                    <TableCell padding="checkbox">
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        {scope.Istestwitness === 1 ? (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <CheckCircleIcon
                                              sx={{
                                                fontSize: "0.8rem",
                                                color: "#ff9800",
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: "0.6rem",
                                                color: "#ff9800",
                                              }}
                                            >
                                              Marked
                                            </Typography>
                                            <IconButton
                                              onClick={() =>
                                                handleUnmarkTestWitness(
                                                  scope.Id,
                                                  `${scope.GroupTested} - ${scope.TypeOfTest}`,
                                                )
                                              }
                                              size="small"
                                              sx={{
                                                color: "#d32f2f",
                                                "&:hover": {
                                                  backgroundColor: "#ffebee",
                                                },
                                                padding: "2px",
                                              }}
                                              title="Remove test witness marking"
                                            >
                                              <DeleteIcon
                                                sx={{ fontSize: "1rem" }}
                                              />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <>
                                            <Checkbox
                                              checked={selectedTestWitnessScopes.includes(
                                                scope.Id,
                                              )}
                                              onChange={(e) =>
                                                handleTestWitnessScopeSelection(
                                                  scope.Id,
                                                  e.target.checked,
                                                )
                                              }
                                              sx={{
                                                color:
                                                  scope.Istestwitness === 1
                                                    ? "#ff9800"
                                                    : "#ccc",
                                                "&.Mui-checked": {
                                                  color: "#ff9800",
                                                },
                                              }}
                                            />
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                                color: "#ff9800",
                                              }}
                                            >
                                              {index + 1}
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                  )}

                                  {/* Adequacy Status Column - Show only when stage is 18, 18a, 18b or 20 */}
                                  {(applicationStage === "18" ||
                                    applicationStage === "18a" ||
                                    applicationStage === "18b" ||
                                    applicationStage === "20") && (
                                    <TableCell align="center">
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        {adequacyChecks[scope.Id]?.status ? (
                                          <Chip
                                            icon={
                                              adequacyChecks[scope.Id]
                                                ?.status === "Adequate" ? (
                                                <CheckCircleIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : adequacyChecks[scope.Id]
                                                  ?.status === "Inadequate" ? (
                                                <ErrorIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : null
                                            }
                                            label={
                                              adequacyChecks[scope.Id]?.status
                                            }
                                            color={
                                              adequacyChecks[scope.Id]
                                                ?.status === "Adequate"
                                                ? "success"
                                                : adequacyChecks[scope.Id]
                                                      ?.status === "Inadequate"
                                                  ? "error"
                                                  : "default"
                                            }
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                          />
                                        ) : (
                                          <Chip
                                            label="Not Assessed"
                                            color="default"
                                            size="small"
                                            variant="outlined"
                                            sx={{ color: "#6c757d" }}
                                          />
                                        )}

                                        {/* Show observation/remarks for Inadequate status */}
                                        {adequacyChecks[scope.Id]?.status ===
                                          "Inadequate" &&
                                          adequacyChecks[scope.Id]?.remarks && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#dc3545",
                                                fontSize: "0.7rem",
                                                textAlign: "center",
                                                maxWidth: 200,
                                                wordBreak: "break-word",
                                                lineHeight: 1.2,
                                                mt: 0.5,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              "
                                              {adequacyChecks[scope.Id].remarks}
                                              "
                                            </Typography>
                                          )}
                                        {adequacyChecks[scope.Id]?.status ===
                                          "Inadequate" &&
                                          adequacyChecks[scope.Id]
                                            ?.clauseNo && (
                                            <Chip
                                              label={`Clause: ${adequacyChecks[scope.Id].clauseNo}`}
                                              size="small"
                                              sx={{
                                                mt: 0.5,
                                                height: "20px",
                                                fontSize: "0.65rem",
                                                backgroundColor: "#ffebee",
                                                color: "#d32f2f",
                                                border: "1px solid #ffcdd2",
                                                "& .MuiChip-label": {
                                                  px: 0.5,
                                                  fontWeight: 500,
                                                },
                                              }}
                                            />
                                          )}
                                      </Box>
                                    </TableCell>
                                  )}

                                  <TableCell>
                                    {scope.subGroupName || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "0.875rem" }}
                                        >
                                          {scope.TestCode || "NA"},
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "0.875rem" }}
                                        >
                                          {scope.GroupTested},
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "0.875rem" }}
                                        >
                                          {scope.TypeOfTest},
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "0.875rem" }}
                                        >
                                          {scope.ScopeStandard}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#6a1b9a",
                                            fontSize: "0.7rem",
                                            minWidth: 60,
                                          }}
                                        >
                                          Type:
                                        </Typography>
                                        <Chip
                                          label={
                                            scope.rangeType || "Quantitative"
                                          }
                                          color={
                                            scope.rangeType === "Qualitative"
                                              ? "secondary"
                                              : scope.rangeType ===
                                                  "Calculative"
                                                ? "warning"
                                                : scope.rangeType === "Others"
                                                  ? "info"
                                                  : "primary"
                                          }
                                          size="small"
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#00695c",
                                            fontSize: "0.7rem",
                                            minWidth: 60,
                                          }}
                                        >
                                          Facility:
                                        </Typography>
                                        {formattedFacilities.length > 0 ? (
                                          formattedFacilities.map((f, i) => (
                                            <Chip
                                              key={i}
                                              label={f}
                                              size="small"
                                              sx={{ mr: 0.5 }}
                                              color={
                                                f === "Permanent Facility"
                                                  ? "primary"
                                                  : f ===
                                                      "POCT (Point of Care Testing)"
                                                    ? "success"
                                                    : "default"
                                              }
                                            />
                                          ))
                                        ) : (
                                          <Typography variant="body2">
                                            N/A
                                          </Typography>
                                        )}
                                      </Box>
                                      {/* Only show Range for Quantitative type */}
                                      {scope.rangeType === "Quantitative" && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#1565c0",
                                              fontSize: "0.7rem",
                                              minWidth: 60,
                                            }}
                                          >
                                            Range:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: "0.875rem" }}
                                          >
                                            {scope.RangeLL || "N/A"}{" "}
                                            {scope.RangeULUnit || "N/A"} to{" "}
                                            {/* {scope.RangeLLUnit || "N/A"} to{" "} */}
                                            {scope.RangeUL || "N/A"}{" "}
                                            {scope.RangeULUnit || "N/A"}
                                          </Typography>
                                        </Box>
                                      )}
                                      {/* Only show Remarks for Qualitative, Calculative, or Others type */}
                                      {(scope.rangeType === "Qualitative" ||
                                        scope.rangeType === "Calculative" ||
                                        scope.rangeType === "Others") && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "#558b2f",
                                              fontSize: "0.7rem",
                                              minWidth: 60,
                                            }}
                                          >
                                            Remarks:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            {scope.rangeRemark || "N/A"}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#f57c00",
                                            fontSize: "0.7rem",
                                            minWidth: 60,
                                          }}
                                        >
                                          @Observed Value / MU:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.875rem" }}
                                        >
                                          {scope.CVMU ||
                                            scope.CVMu ||
                                            scope.cvmu ||
                                            (scope.cv && scope.mu
                                              ? `@Observed Value: ${scope.cv}, MU: ${scope.mu}`
                                              : scope.cv
                                                ? `@Observed Value: ${scope.cv}`
                                                : scope.mu
                                                  ? `MU: ${scope.mu}`
                                                  : "N/A")}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {scope.EquipmentName || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {scope.ReferenceMaterialName || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {scope.Pt_id && scope.PtProgrammeName ? (
                                      <Box>
                                        {scope.Pt_id.split(",").map(
                                          (ptId, index) => {
                                            const ptNames =
                                              scope.PtProgrammeName.split(",");
                                            const ptName =
                                              ptNames[index]?.trim() || "PT";
                                            const trimmedPtId = ptId.trim();

                                            return (
                                              <Box
                                                key={index}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb:
                                                    index <
                                                    scope.Pt_id.split(",")
                                                      .length -
                                                      1
                                                      ? 1
                                                      : 0,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  {ptName}
                                                </Typography>
                                                {(ptName === "PT" ||
                                                  ptName === "PT (Others)") && (
                                                  <Button
                                                    size="small"
                                                    variant={
                                                      ptScoresMap[
                                                        `${scope.Id}_${trimmedPtId}`
                                                      ]
                                                        ? "contained"
                                                        : "outlined"
                                                    }
                                                    color={
                                                      ptScoresMap[
                                                        `${scope.Id}_${trimmedPtId}`
                                                      ]
                                                        ? "success"
                                                        : "primary"
                                                    }
                                                    onClick={() =>
                                                      handleOpenPerformanceScoreDialog(
                                                        scope,
                                                        trimmedPtId,
                                                        ptName,
                                                      )
                                                    }
                                                    startIcon={
                                                      ptScoresMap[
                                                        `${scope.Id}_${trimmedPtId}`
                                                      ] ? (
                                                        <CheckCircleIcon />
                                                      ) : null
                                                    }
                                                    sx={{
                                                      minWidth: "auto",
                                                      px: 1,
                                                      py: 0.5,
                                                    }}
                                                  >
                                                    Enter Details
                                                  </Button>
                                                )}
                                              </Box>
                                            );
                                          },
                                        )}
                                      </Box>
                                    ) : (
                                      "N/A"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={scope.PTAvailable || "N/A"}
                                      color={
                                        scope.PTAvailable === "Yes"
                                          ? "success"
                                          : "default"
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {getFilteredExistingScopes().length === 0 &&
                              !existingScopesLoading && (
                                <TableRow>
                                  <TableCell
                                    colSpan={
                                      applicationStage === null ||
                                      applicationStage === "" ||
                                      applicationStage === "18a"
                                        ? 6
                                        : 5
                                    }
                                    align="center"
                                    sx={{ py: 4 }}
                                  >
                                    <Typography color="text.secondary">
                                      {existingScopes.length === 0
                                        ? applicationStage === "18" ||
                                          applicationStage === "18b" ||
                                          applicationStage === "20"
                                          ? "No scopes available for adequacy check."
                                          : 'No scopes added yet. Click "Add Scope" to get started.'
                                        : "No scopes match the current filters."}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination Controls */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={existingScopesPage === 1}
                          onClick={() => {
                            const newPage = existingScopesPage - 1;
                            setExistingScopesPage(newPage);
                            loadExistingScopes(
                              newPage,
                              existingScopesPageSize,
                              existingScopesSearchQuery,
                            );
                          }}
                        >
                          Previous
                        </Button>

                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          {/* Page Number Buttons */}
                          {[
                            ...Array(Math.min(5, existingScopesTotalPages)),
                          ].map((_, index) => {
                            const startPage = Math.max(
                              1,
                              existingScopesPage - 2,
                            );
                            const pageNum = startPage + index;

                            if (pageNum > existingScopesTotalPages) return null;

                            return (
                              <Button
                                key={pageNum}
                                variant={
                                  pageNum === existingScopesPage
                                    ? "contained"
                                    : "outlined"
                                }
                                size="small"
                                onClick={() => {
                                  setExistingScopesPage(pageNum);
                                  loadExistingScopes(
                                    pageNum,
                                    existingScopesPageSize,
                                    existingScopesSearchQuery,
                                  );
                                }}
                                sx={{ minWidth: 40 }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          {existingScopesTotalPages > 5 &&
                            existingScopesPage <
                              existingScopesTotalPages - 2 && (
                              <>
                                <Typography>...</Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setExistingScopesPage(
                                      existingScopesTotalPages,
                                    );
                                    loadExistingScopes(
                                      existingScopesTotalPages,
                                      existingScopesPageSize,
                                      existingScopesSearchQuery,
                                    );
                                  }}
                                  sx={{ minWidth: 40 }}
                                >
                                  {existingScopesTotalPages}
                                </Button>
                              </>
                            )}
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          disabled={
                            existingScopesPage === existingScopesTotalPages
                          }
                          onClick={() => {
                            const newPage = existingScopesPage + 1;
                            setExistingScopesPage(newPage);
                            loadExistingScopes(
                              newPage,
                              existingScopesPageSize,
                              existingScopesSearchQuery,
                            );
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
        </Box>
      </Paper>

      {/* Add/Edit Scope Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Add Scopes from Master Data</Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search scopes..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                sx={{ width: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sub Group</InputLabel>
                <Select
                  value={selectedSubGroupMaster}
                  onChange={(e) => handleSearchSubGroup(e.target.value)}
                  label="Sub Group"
                  disabled={subGroupsLoading}
                >
                  <MenuItem value="">All</MenuItem>
                  {subGroups.map((group) => (
                    <MenuItem key={group.sGrpId} value={group.sGrpId}>
                      {group.subGrpName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1);
                    loadScopeMasterData(1, searchQuery);
                  }}
                  label="Per Page"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {scopeMasterLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {scopeMasterData.length} of {totalRecords} results
                </Typography>
                <Typography variant="body2" color="primary">
                  {selectedScopes.length} selected
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={
                            selectedScopes.length === scopeMasterData.length &&
                            scopeMasterData.length > 0
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Test Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sub Group</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Materials or Products tested
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Component, parameter or characteristic tested
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Test Method Specification
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Qualitative/Quantitative
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        PT Availability in Market
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scopeMasterData.map((scope) => (
                      <TableRow
                        key={scope.Id}
                        sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                      >
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedScopes.includes(scope.Id)}
                            onChange={(e) =>
                              handleScopeSelection(scope.Id, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell>{scope.TestCode}</TableCell>
                        <TableCell>{scope.SubGroupName}</TableCell>
                        <TableCell>{scope.GroupTested}</TableCell>
                        <TableCell>{scope.TypeOfTest}</TableCell>
                        <TableCell>{scope.ScopeStandard}</TableCell>
                        <TableCell>
                          <Chip
                            label={scope.Category}
                            size="small"
                            color={
                              scope.Category === "Quantitative"
                                ? "primary"
                                : "secondary"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={scope.PTAvailable}
                            size="small"
                            color={
                              scope.PTAvailable === "Yes"
                                ? "success"
                                : "default"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {scopeMasterData.length === 0 && !scopeMasterLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No scope data found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 2,
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, currentPage - 2) + i;
                      if (pageNum > totalPages) return null;

                      return (
                        <Button
                          key={pageNum}
                          size="small"
                          variant={
                            pageNum === currentPage ? "contained" : "outlined"
                          }
                          onClick={() => handlePageChange(pageNum)}
                          sx={{ minWidth: 40 }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </Box>

                  <Button
                    size="small"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>

                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a") && (
            <Button
              onClick={handleAddSelectedScopes}
              variant="contained"
              disabled={selectedScopes.length === 0}
              startIcon={<SaveIcon />}
            >
              Add Selected ({selectedScopes.length})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Scope Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "80vh" },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Edit Scope</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {/* Read-only fields */}
            <Card sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Scope Information (Read-only)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Test Code"
                    value={editReadOnlyData.TestCode || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Discipline Name"
                    value={editReadOnlyData.disciplineName || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Materials/Products Tested"
                    value={editReadOnlyData.GroupTested || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Specific Test Performed"
                    value={editReadOnlyData.TypeOfTest || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Test Method / Equipment Used"
                    value={editReadOnlyData.ScopeStandard || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Editable fields */}
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Editable Information
              </Typography>
              <Grid container spacing={2}>
                {/* Range Type Selector - Read Only */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Range Type"
                    value={editFormData.rangeType || "Quantitative"}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "text.primary",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Grid>

                {/* Show Range fields only for Quantitative */}
                {editFormData.rangeType === "Quantitative" && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Range Lower Limit"
                        type="number"
                        value={editFormData.RangeLL || ""}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            RangeLL: e.target.value,
                          }))
                        }
                        fullWidth
                        size="small"
                        required
                        error={
                          editFormData.rangeType === "Quantitative" &&
                          (editFormData.RangeLL === "" ||
                            (editFormData.RangeUL !== "" &&
                              Number(editFormData.RangeLL) >=
                                Number(editFormData.RangeUL)))
                        }
                        helperText={
                          editFormData.rangeType === "Quantitative" &&
                          editFormData.RangeLL === ""
                            ? "Required"
                            : editFormData.RangeUL !== "" &&
                                Number(editFormData.RangeLL) >=
                                  Number(editFormData.RangeUL)
                              ? "Lower Limit must be less than Upper Limit"
                              : ""
                        }
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={3}>
                      <TextField
                        label="Range LL Unit"
                        value={editFormData.RangeLLUnit || ""}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            RangeLLUnit: e.target.value,
                          }))
                        }
                        fullWidth
                        size="small"
                        required
                        error={
                          editFormData.rangeType === "Quantitative" &&
                          !editFormData.RangeLLUnit
                        }
                        helperText={
                          editFormData.rangeType === "Quantitative" &&
                          !editFormData.RangeLLUnit
                            ? "Required"
                            : ""
                        }
                      />
                    </Grid> */}
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Range Upper Limit"
                        type="number"
                        value={editFormData.RangeUL || ""}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            RangeUL: e.target.value,
                          }))
                        }
                        fullWidth
                        size="small"
                        required
                        error={
                          editFormData.rangeType === "Quantitative" &&
                          (editFormData.RangeUL === "" ||
                            (editFormData.RangeLL !== "" &&
                              Number(editFormData.RangeUL) <=
                                Number(editFormData.RangeLL)))
                        }
                        helperText={
                          editFormData.rangeType === "Quantitative" &&
                          editFormData.RangeUL === ""
                            ? "Required"
                            : editFormData.RangeLL !== "" &&
                                Number(editFormData.RangeUL) <=
                                  Number(editFormData.RangeLL)
                              ? "Upper Limit must be greater than Lower Limit"
                              : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Unit of measurement"
                        value={editFormData.RangeULUnit || ""}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            RangeULUnit: e.target.value,
                          }))
                        }
                        fullWidth
                        size="small"
                        required
                        error={
                          editFormData.rangeType === "Quantitative" &&
                          !editFormData.RangeULUnit
                        }
                        helperText={
                          editFormData.rangeType === "Quantitative" &&
                          !editFormData.RangeULUnit
                            ? "Required"
                            : ""
                        }
                      />
                    </Grid>
                  </>
                )}

                {/* Show Range Remarks only for Qualitative, Calculative, and Others */}
                {(editFormData.rangeType === "Qualitative" ||
                  editFormData.rangeType === "Calculative" ||
                  editFormData.rangeType === "Others") && (
                  <Grid item xs={12}>
                    <TextField
                      label="Range Remarks"
                      value={editFormData.rangeRemark || ""}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          rangeRemark: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      required
                      error={
                        (editFormData.rangeType === "Qualitative" ||
                          editFormData.rangeType === "Calculative" ||
                          editFormData.rangeType === "Others") &&
                        !editFormData.rangeRemark
                      }
                      helperText={
                        (editFormData.rangeType === "Qualitative" ||
                          editFormData.rangeType === "Calculative" ||
                          editFormData.rangeType === "Others") &&
                        !editFormData.rangeRemark
                          ? "Required"
                          : ""
                      }
                      placeholder="Enter remarks for qualitative, calculative, or other range types"
                    />
                  </Grid>
                )}

                {/* <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Test Performed At</InputLabel>
                    <Select
                      value={editFormData.testPerformedAt || ""}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          testPerformedAt: e.target.value,
                        }))
                      }
                      label="Test Performed At"
                    >
                      <MenuItem value="Permanent Facility">
                        Permanent Facility
                      </MenuItem>
                      <MenuItem value="Site Facility">Site Facility</MenuItem>
                      <MenuItem value="Mobile Facility">
                        Mobile Facility
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="MU"
                    value={editFormData.mu || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        mu: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Enter measurement uncertainty"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="@Observed Value"
                    value={editFormData.cv || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        cv: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    placeholder="Enter observed value"
                    error={
                      editFormData.rangeType === "Quantitative" &&
                      editFormData.cv !== "" &&
                      editFormData.RangeLL !== "" &&
                      editFormData.RangeUL !== "" &&
                      (parseFloat(editFormData.cv) <
                        parseFloat(editFormData.RangeLL) ||
                        parseFloat(editFormData.cv) >
                          parseFloat(editFormData.RangeUL))
                    }
                    helperText={
                      editFormData.rangeType === "Quantitative" &&
                      editFormData.cv !== "" &&
                      editFormData.RangeLL !== "" &&
                      editFormData.RangeUL !== "" &&
                      (parseFloat(editFormData.cv) <
                        parseFloat(editFormData.RangeLL) ||
                        parseFloat(editFormData.cv) >
                          parseFloat(editFormData.RangeUL))
                        ? `Observed Value must be between ${editFormData.RangeLL} and ${editFormData.RangeUL}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Test Performed At</InputLabel>
                    <Select
                      multiple
                      value={selectedValues}
                      onChange={(e) => {
                        const value = e.target.value;

                        setEditFormData((prev) => ({
                          ...prev,
                          testPerformedAt: value.join(", "),
                        }));
                      }}
                    >
                      {facilities.map((facility) => (
                        <MenuItem
                          key={facility.FacilityName}
                          value={facility.FacilityName}
                        >
                          <Checkbox
                            checked={selectedValues.includes(
                              facility.FacilityName,
                            )}
                          />
                          <Typography>{facility.FacilityLabel}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateScope}
            variant="contained"
            disabled={
              editLoading ||
              // Disable if Quantitative and required fields are missing
              (editFormData.rangeType === "Quantitative" &&
                (!editFormData.RangeLL ||
                  !editFormData.RangeUL ||
                  !editFormData.RangeULUnit)) ||
              // Disable if Qualitative/Calculative/Others and remarks is missing
              ((editFormData.rangeType === "Qualitative" ||
                editFormData.rangeType === "Calculative" ||
                editFormData.rangeType === "Others") &&
                !editFormData.rangeRemark?.trim())
            }
            startIcon={
              editLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
          >
            {editLoading ? "Updating..." : "Update Scope"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon sx={{ color: "#d32f2f" }} />
            <Typography variant="h6">Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {deleteTarget === "single"
              ? "Are you sure you want to delete this scope? This action cannot be undone."
              : `Are you sure you want to delete ${selectedExistingScopes.length} selected scope(s)? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={existingScopesLoading}
          >
            {existingScopesLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Witness Confirmation Dialog */}
      <Dialog
        open={testWitnessConfirmOpen}
        onClose={handleCloseTestWitnessModal}
        aria-labelledby="test-witness-dialog-title"
        aria-describedby="test-witness-dialog-description"
      >
        <DialogTitle id="test-witness-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon sx={{ color: "#ff9800" }} />
            <Typography variant="h6">Mark for Test Witness</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography id="test-witness-dialog-description">
            Are you sure you want to mark {selectedTestWitnessScopes.length}{" "}
            selected scope(s) for test witness? This will update the
            Istestwitness column to 1 for these scopes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseTestWitnessModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTestWitness}
            variant="contained"
            color="warning"
            startIcon={<CheckCircleIcon />}
            disabled={testWitnessLoading}
            sx={{
              backgroundColor: "#ff9800",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            {testWitnessLoading ? "Marking..." : "Yes, Mark for Test Witness"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Other Scope Modal */}
      <Dialog
        open={addOtherModalOpen}
        onClose={handleCloseOtherModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: 600,
          }}
        >
          ➕ Add Other Scope
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                📋 Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Materials/Products Tested"
                value={otherScopeForm.groupTested}
                onChange={(e) =>
                  handleOtherFormChange("groupTested", e.target.value)
                }
                placeholder="Materials or products tested"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Specific Test Performed"
                value={otherScopeForm.typeOfTest}
                onChange={(e) =>
                  handleOtherFormChange("typeOfTest", e.target.value)
                }
                placeholder="Type of test"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Test Method / Equipment Used"
                value={otherScopeForm.scopeStandard}
                onChange={(e) =>
                  handleOtherFormChange("scopeStandard", e.target.value)
                }
                placeholder="Test method or equipment used"
                multiline
                rows={2}
              />
            </Grid>

            {/* Range and Detection Settings */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#7c3aed" }}>
                🔍 Range & Detection Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Range Type</InputLabel>
                <Select
                  value={otherScopeForm.rangeType}
                  onChange={(e) =>
                    handleOtherFormChange("rangeType", e.target.value)
                  }
                  label="Range Type"
                >
                  <MenuItem value="Quantitative">Quantitative</MenuItem>
                  <MenuItem value="Qualitative">Qualitative</MenuItem>
                  <MenuItem value="Calculative">Calculative</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Info Alert for Range Type */}
            <Grid item xs={12}>
              <Alert
                severity="info"
                sx={{
                  mt: 1,
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Typography variant="body2">
                  <strong>Range Type: {otherScopeForm.rangeType}</strong>
                  <br />
                  {otherScopeForm.rangeType === "Quantitative"
                    ? "📊 You will need to specify: Lower Limit, Lower Limit Unit, Upper Limit, and Upper Limit Unit"
                    : "📝 You will need to provide: Qualitative Remarks"}
                </Typography>
              </Alert>
            </Grid>

            {/* Conditional Range Details based on Range Type */}
            {otherScopeForm.rangeType === "Quantitative" && (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "#1976d2", fontWeight: 600 }}
                  >
                    Quantitative Range Details
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="Specify Lower Limit"
                    type="number"
                    value={otherScopeForm.rangeLL}
                    onChange={(e) =>
                      handleOtherFormChange("rangeLL", e.target.value)
                    }
                    placeholder="Lower limit"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="Specify Unit"
                    value={otherScopeForm.rangeLLUnit}
                    onChange={(e) =>
                      handleOtherFormChange("rangeLLUnit", e.target.value)
                    }
                    placeholder="Unit for lower limit"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="Specify Upper Limit"
                    type="number"
                    value={otherScopeForm.rangeUL}
                    onChange={(e) =>
                      handleOtherFormChange("rangeUL", e.target.value)
                    }
                    placeholder="Upper limit"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="Specify Unit"
                    value={otherScopeForm.rangeULUnit}
                    onChange={(e) =>
                      handleOtherFormChange("rangeULUnit", e.target.value)
                    }
                    placeholder="Unit for upper limit"
                  />
                </Grid>
              </>
            )}

            {/* Conditional Qualitative Field */}
            {(otherScopeForm.rangeType === "Qualitative" ||
              otherScopeForm.rangeType === "Calculative") && (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "#1976d2", fontWeight: 600 }}
                  >
                    {otherScopeForm.rangeType === "Calculative"
                      ? "Calculative Range Details"
                      : "Qualitative Range Details"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Remarks"
                    value={otherScopeForm.qualitativeRemarks}
                    onChange={(e) =>
                      handleOtherFormChange(
                        "qualitativeRemarks",
                        e.target.value,
                      )
                    }
                    placeholder={
                      otherScopeForm.rangeType === "Calculative"
                        ? "Enter calculative remarks"
                        : "Enter qualitative remarks"
                    }
                    multiline
                    rows={3}
                    helperText={
                      otherScopeForm.rangeType === "Calculative"
                        ? "Describe the calculative aspects or criteria for this test"
                        : "Describe the qualitative aspects or criteria for this test"
                    }
                  />
                </Grid>
              </>
            )}

            {/* Measurement Uncertainty */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Measurement Uncertainty (MU)"
                type="number"
                value={otherScopeForm.mu}
                onChange={(e) => handleOtherFormChange("mu", e.target.value)}
                placeholder="MU value"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CV (%CV)"
                value={otherScopeForm.cv}
                onChange={(e) => handleOtherFormChange("cv", e.target.value)}
                placeholder="CV or %CV"
              />
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#16a34a" }}>
                ℹ️ Additional Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Test Performed At</InputLabel>
                <Select
                  value={otherScopeForm.testPerformedAt}
                  onChange={(e) =>
                    handleOtherFormChange("testPerformedAt", e.target.value)
                  }
                  label="Test Performed At"
                  disabled={facilitiesLoading}
                >
                  {facilitiesLoading ? (
                    <MenuItem value="">Loading facilities...</MenuItem>
                  ) : facilities.length > 0 ? (
                    facilities.map((facility) => (
                      <MenuItem
                        key={facility.FacilityName}
                        value={facility.FacilityName}
                      >
                        {facility.FacilityLabel}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No facilities available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseOtherModal}
            startIcon={<CancelIcon />}
            disabled={otherScopeLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitOtherScope}
            startIcon={
              otherScopeLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
            disabled={otherScopeLoading}
            sx={{
              minWidth: 200,
            }}
          >
            {otherScopeLoading ? "Adding Scope..." : "Add Scope"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Update Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: "#1976d2" }}>
                📋 Bulk Update Preview & Edit
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                File: {previewFileName} | Rows: {editedPreviewData.length}
                {hasUnsavedChanges && (
                  <Typography component="span" sx={{ color: "orange", ml: 1 }}>
                    • Ready for validation and editing
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {hasUnsavedChanges && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveEdit}
                  startIcon={<SaveIcon />}
                  sx={{ backgroundColor: "#2e7d32" }}
                >
                  Validate & Save
                </Button>
              )}
              <IconButton
                onClick={handleClosePreviewModal}
                sx={{ color: "#666" }}
                disabled={Object.keys(validationErrors).length > 0}
                title={
                  Object.keys(validationErrors).length > 0
                    ? "Fix validation errors before closing"
                    : "Close"
                }
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {editedPreviewData.length > 0 ? (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* Instructions */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: "medium" }}
                >
                  📝 <strong>Edit Instructions:</strong>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    fontSize: "0.875rem",
                    color: "#64748b",
                  }}
                >
                  <Typography variant="body2">
                    🔒 <strong>Read-only:</strong> ID/Code columns cannot be
                    edited
                  </Typography>
                  <Typography variant="body2">
                    📋 <strong>Dropdowns:</strong> Click dropdown columns to
                    select values
                  </Typography>
                  <Typography variant="body2">
                    ⚠️ <strong>Required:</strong> Red asterisk (*) fields must
                    be filled
                  </Typography>
                  <Typography variant="body2">
                    🏢 <strong>Facility Type:</strong> To bind Facility Type,
                    first check the checkbox, then select the facility from the
                    Facility dropdown, and click <strong>Apply</strong>. Your
                    selected facility will be bound to the selected parameters.
                    After that, click <strong>Validate & Save</strong> for
                    confirmation.
                  </Typography>
                  <Typography variant="body2">
                    💾 <strong>Save:</strong> Click "Save Changes" after editing
                    to validate
                  </Typography>
                  {editedPreviewData.length > 100 && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#dc2626", fontWeight: "medium" }}
                    >
                      • You can only edit the first 100 rows in preview
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Facility Type</InputLabel>

                      <Select
                        multiple
                        value={commonFacilityType}
                        disabled={facilitiesLoading}
                        onChange={(e) => setCommonFacilityType(e.target.value)}
                        label="Facility Type"
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {facilitiesLoading ? (
                          <MenuItem value="">Loading facilities...</MenuItem>
                        ) : facilities.length > 0 ? (
                          facilities.map((facility) => (
                            <MenuItem
                              key={facility.FacilityName}
                              value={facility.FacilityName}
                            >
                              {facility.FacilityLabel}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="">No facilities available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      onClick={applyFacilityToSelected}
                      disabled={
                        selectedRows.length === 0 || !commonFacilityType
                      }
                    >
                      Apply
                    </Button>
                  </Grid>

                  <Grid item>
                    <Typography>{selectedRows.length} selected</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Table Container */}
              <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <TableContainer sx={{ height: "100%" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedRows.length === editedPreviewData.length
                            }
                            onChange={(e) =>
                              handleSelectAllRows(e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "#f1f5f9",
                            minWidth: 60,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          #
                        </TableCell>
                        {previewHeaders
                          .filter((header) => !isHiddenFromPreview(header))
                          .map((header, index) => (
                            <TableCell
                              key={index}
                              sx={{
                                fontWeight: 600,
                                backgroundColor: isNonEditableColumn(header)
                                  ? "#e0e0e0"
                                  : isDateColumn(header)
                                    ? "#e3f2fd"
                                    : "#f5f5f5",
                                minWidth: 150,
                                border: "1px solid #e0e0e0",
                                color: isNonEditableColumn(header)
                                  ? "#666666"
                                  : isDateColumn(header)
                                    ? "#1565c0"
                                    : "inherit",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span>
                                  {isNonEditableColumn(header)
                                    ? "🔒 "
                                    : isDropdownColumn(header)
                                      ? "📋 "
                                      : isDateColumn(header)
                                        ? "📅 "
                                        : ""}
                                  {header || `Column ${index + 1}`}
                                  {isRequiredColumn(header) && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#d32f2f",
                                        fontWeight: "bold",
                                        ml: 0.5,
                                      }}
                                    >
                                      *
                                    </Typography>
                                  )}
                                </span>
                              </Box>
                              {isNonEditableColumn(header) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.7rem",
                                    color: "#888888",
                                    fontStyle: "italic",
                                  }}
                                >
                                  (Read-only)
                                </Typography>
                              )}
                              {isDropdownColumn(header) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.7rem",
                                    color: "#1976d2",
                                    fontStyle: "italic",
                                  }}
                                >
                                  (Dropdown)
                                </Typography>
                              )}
                              {isRequiredColumn(header) &&
                                !isNonEditableColumn(header) &&
                                !isDropdownColumn(header) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      fontSize: "0.7rem",
                                      color: "#d32f2f",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    (Required)
                                  </Typography>
                                )}
                            </TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editedPreviewData.slice(0, 100).map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            backgroundColor:
                              rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                            "&:hover": { backgroundColor: "#fff3e0" },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRows.includes(rowIndex)}
                              onChange={(e) =>
                                handleRowSelect(rowIndex, e.target.checked)
                              }
                            />
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", color: "#1976d2" }}
                          >
                            {rowIndex + 1}
                          </TableCell>
                          {previewHeaders.map((header, colIndex) => {
                            // Skip hidden columns
                            if (isHiddenFromPreview(header)) {
                              return null;
                            }

                            const hasError =
                              validationErrors[rowIndex] &&
                              validationErrors[rowIndex][colIndex];
                            const errorMessage = hasError
                              ? validationErrors[rowIndex][colIndex]
                              : "";

                            // Dynamic required field calculation based on range type
                            const rangeTypeColIndex = previewHeaders.findIndex(
                              (h) =>
                                h?.toLowerCase().includes("range type") ||
                                h?.toLowerCase().includes("rangetype"),
                            );
                            const rangeType =
                              rangeTypeColIndex !== -1
                                ? row[rangeTypeColIndex]
                                : "";
                            const headerLower = header?.toLowerCase() || "";

                            const alwaysRequired =
                              headerLower.includes("range type") ||
                              headerLower.includes("rangetype");
                            const quantitativeRequired =
                              rangeType === "Quantitative" &&
                              (headerLower.includes("range ll") ||
                                headerLower.includes("rangell") ||
                                headerLower.includes("range lower limit") ||
                                headerLower.includes("rangelowerlimit") ||
                                //headerLower.includes("range ll unit") ||
                                headerLower.includes("rangellunit") ||
                                headerLower.includes("range ul") ||
                                headerLower.includes("rangeul") ||
                                headerLower.includes("range upper limit") ||
                                headerLower.includes("rangeupperlimit") ||
                                headerLower.includes("range ul unit") ||
                                headerLower.includes("rangeulunit"));
                            const qualitativeRequired =
                              (rangeType === "Qualitative" ||
                                rangeType === "Calculative" ||
                                rangeType === "Others") &&
                              (headerLower.includes("range remarks") ||
                                headerLower.includes("rangeremarks") ||
                                headerLower.includes("qualitative remarks") ||
                                headerLower.includes("qualitativeremarks") ||
                                headerLower.includes("calculative remarks") ||
                                headerLower.includes("calculativeremarks") ||
                                (headerLower.includes("remarks") &&
                                  !headerLower.includes("range")));
                            // const dropdownRequired =
                            //   headerLower.includes("facility type") ||
                            //   headerLower.includes("test performed at") ||
                            //   headerLower.includes("testperformedat");
                            const dropdownRequired =
                              headerLower.includes("test performed at");

                            const isRequired =
                              alwaysRequired ||
                              quantitativeRequired ||
                              qualitativeRequired ||
                              dropdownRequired;

                            return (
                              <TableCell
                                key={colIndex}
                                sx={{
                                  border: "1px solid #e0e0e0",
                                  maxWidth: 200,
                                  padding: "4px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  backgroundColor: isNonEditableColumn(header)
                                    ? "#f5f5f5"
                                    : hasError
                                      ? "#ffebee"
                                      : "inherit",
                                  borderColor: hasError ? "#f44336" : "#e0e0e0",
                                  borderWidth: hasError ? "2px" : "1px",
                                }}
                                title={row[colIndex] || ""}
                              >
                                {isFacilityColumn(header) ? (
                                  <Select
                                    multiple
                                    value={
                                      Array.isArray(row[colIndex])
                                        ? row[colIndex]
                                        : (row[colIndex] || "")
                                            .split(",")
                                            .map((v) => v.trim())
                                            .filter(Boolean)
                                    }
                                    onChange={(e) =>
                                      handleCellValueChange(
                                        rowIndex,
                                        colIndex,
                                        e.target.value,
                                      )
                                    }
                                    renderValue={(selected) =>
                                      selected.join(", ")
                                    }
                                    size="small"
                                  >
                                    {[
                                      {
                                        label: "Permanent Facility",
                                        value: "PermanentFacility",
                                      },
                                      {
                                        label: "Temporary Facility",
                                        value: "TemporaryFacility",
                                      },
                                      {
                                        label: "Site of Customer",
                                        value: "SiteOfCustomer",
                                      },
                                    ].map((item) => (
                                      <MenuItem
                                        key={item.value}
                                        value={item.value}
                                      >
                                        <Checkbox
                                          checked={(Array.isArray(row[colIndex])
                                            ? row[colIndex]
                                            : (row[colIndex] || "").split(",")
                                          ).includes(item.value)}
                                        />
                                        <Typography>{item.label}</Typography>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                ) : isDropdownColumn(header) ? (
                                  <FormControl
                                    size="small"
                                    fullWidth
                                    error={!!hasError}
                                    sx={{
                                      "& .MuiInputBase-root": {
                                        fontSize: "0.875rem",
                                        minHeight: "auto",
                                        backgroundColor: hasError
                                          ? "#ffebee"
                                          : "white",
                                        borderColor: hasError
                                          ? "#f44336"
                                          : "#e0e0e0",
                                      },
                                    }}
                                  >
                                    <Select
                                      value={(() => {
                                        const currentValue =
                                          row[colIndex] || "";
                                        const headerLower =
                                          header?.toLowerCase() || "";
                                        const validFacilityTypes = [
                                          "PermanentFacility",
                                          "TemporaryFacility",
                                          "SiteOfCustomer",
                                        ];
                                        const validRangeTypes = [
                                          "Quantitative",
                                          "Qualitative",
                                          "Calculative",
                                          "Others",
                                        ];

                                        // Handle facility type - only bind if it matches valid options or known mappings
                                        if (
                                          headerLower.includes(
                                            "facility type",
                                          ) ||
                                          headerLower.includes(
                                            "test performed at",
                                          )
                                        ) {
                                          // Known mappings that we accept
                                          if (currentValue === "Laboratory")
                                            return "PermanentFacility";
                                          if (currentValue === "Site")
                                            return "SiteOfCustomer";
                                          if (
                                            currentValue ===
                                            "Permanent Facility"
                                          )
                                            return "PermanentFacility";
                                          if (
                                            currentValue ===
                                            "Temporary Facility"
                                          )
                                            return "TemporaryFacility";
                                          if (
                                            currentValue === "Site of Customer"
                                          )
                                            return "SiteOfCustomer";

                                          // Only bind if exact match to valid values
                                          if (
                                            validFacilityTypes.includes(
                                              currentValue,
                                            )
                                          ) {
                                            return currentValue;
                                          }

                                          // Invalid value - don't bind, return empty
                                          return "";
                                        }

                                        // Handle Range Type - only bind if it matches valid options or known mappings
                                        if (
                                          headerLower.includes("range type") ||
                                          headerLower.includes("rangetype")
                                        ) {
                                          // Known variations that we accept
                                          const normalizedValue =
                                            currentValue.toLowerCase();
                                          if (
                                            normalizedValue ===
                                              "quantitative" ||
                                            normalizedValue === "qty"
                                          )
                                            return "Quantitative";
                                          if (
                                            normalizedValue === "qualitative" ||
                                            normalizedValue === "qual"
                                          )
                                            return "Qualitative";
                                          if (
                                            normalizedValue === "calculative" ||
                                            normalizedValue === "calc"
                                          )
                                            return "Calculative";
                                          if (
                                            normalizedValue === "others" ||
                                            normalizedValue === "other"
                                          )
                                            return "Others";

                                          // Only bind if exact match to valid values
                                          if (
                                            validRangeTypes.includes(
                                              currentValue,
                                            )
                                          ) {
                                            return currentValue;
                                          }

                                          // Invalid value - don't bind, return empty
                                          return "";
                                        }

                                        return currentValue;
                                      })()}
                                      onChange={(e) =>
                                        handleCellValueChange(
                                          rowIndex,
                                          colIndex,
                                          e.target.value,
                                        )
                                      }
                                      displayEmpty
                                      variant="outlined"
                                      sx={{
                                        "& .MuiSelect-select": {
                                          padding: "6px 8px",
                                        },
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        <em style={{ color: "#999" }}>
                                          {(() => {
                                            const currentValue =
                                              row[colIndex] || "";
                                            const headerLower =
                                              header?.toLowerCase() || "";

                                            if (currentValue.trim() === "") {
                                              return headerLower.includes(
                                                "range type",
                                              )
                                                ? "Select Range Type (Required)"
                                                : headerLower.includes(
                                                      "facility type",
                                                    ) ||
                                                    headerLower.includes(
                                                      "test performed at",
                                                    )
                                                  ? "Select Facility Type (Required)"
                                                  : "Select an option (Required)";
                                            } else {
                                              // Show original Excel value even if invalid
                                              const originalValue =
                                                originalPreviewData[rowIndex] &&
                                                originalPreviewData[rowIndex][
                                                  colIndex
                                                ]
                                                  ? originalPreviewData[
                                                      rowIndex
                                                    ][colIndex]
                                                  : currentValue;
                                              return `Excel: "${originalValue}" - Invalid value, choose valid option below`;
                                            }
                                          })()}
                                        </em>
                                      </MenuItem>
                                      {(() => {
                                        const headerLower =
                                          header?.toLowerCase() || "";

                                        if (
                                          headerLower.includes("range type") ||
                                          headerLower.includes("rangetype")
                                        ) {
                                          return [
                                            <MenuItem
                                              key="quantitative"
                                              value="Quantitative"
                                            >
                                              Quantitative
                                            </MenuItem>,
                                            <MenuItem
                                              key="qualitative"
                                              value="Qualitative"
                                            >
                                              Qualitative
                                            </MenuItem>,
                                            <MenuItem
                                              key="calculative"
                                              value="Calculative"
                                            >
                                              Calculative
                                            </MenuItem>,
                                            <MenuItem
                                              key="others"
                                              value="Others"
                                            >
                                              Others
                                            </MenuItem>,
                                          ];
                                        } else if (
                                          headerLower.includes(
                                            "facility type",
                                          ) ||
                                          headerLower.includes(
                                            "test performed at",
                                          ) ||
                                          headerLower.includes(
                                            "testperformedat",
                                          )
                                        ) {
                                          return [
                                            <MenuItem
                                              key="permanent"
                                              value="PermanentFacility"
                                            >
                                              Permanent Facility
                                            </MenuItem>,
                                            <MenuItem
                                              key="temporary"
                                              value="TemporaryFacility"
                                            >
                                              Temporary Facility
                                            </MenuItem>,
                                            <MenuItem
                                              key="customer"
                                              value="SiteOfCustomer"
                                            >
                                              Site of Customer
                                            </MenuItem>,
                                          ];
                                        } else {
                                          // Default fallback options
                                          return [
                                            <MenuItem
                                              key="default1"
                                              value="PermanentFacility"
                                            >
                                              Permanent Facility
                                            </MenuItem>,
                                            <MenuItem
                                              key="default2"
                                              value="TemporaryFacility"
                                            >
                                              Temporary Facility
                                            </MenuItem>,
                                            <MenuItem
                                              key="default3"
                                              value="SiteOfCustomer"
                                            >
                                              Site of Customer
                                            </MenuItem>,
                                          ];
                                        }
                                      })()}
                                    </Select>
                                    {hasError && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#d32f2f",
                                          fontSize: "0.7rem",
                                          mt: 0.5,
                                        }}
                                      >
                                        {errorMessage}
                                      </Typography>
                                    )}
                                    {!hasError && isRequired && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#666",
                                          fontSize: "0.7rem",
                                          mt: 0.5,
                                        }}
                                      >
                                        Required *
                                      </Typography>
                                    )}
                                    {!hasError && isDropdownColumn(header) && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#d32f2f", // Red for required dropdown fields
                                          fontSize: "0.7rem",
                                          mt: 0.5,
                                        }}
                                      >
                                        Required:{" "}
                                        {headerLower.includes("range type")
                                          ? "Select Range Type *"
                                          : headerLower.includes(
                                                "facility type",
                                              ) ||
                                              headerLower.includes(
                                                "test performed at",
                                              )
                                            ? "Select Facility Type *"
                                            : "Select Option *"}
                                      </Typography>
                                    )}
                                    {hasError && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#d32f2f",
                                          fontSize: "0.7rem",
                                          mt: 0.5,
                                        }}
                                      >
                                        {errorMessage}
                                      </Typography>
                                    )}
                                  </FormControl>
                                ) : (
                                  <TextField
                                    value={
                                      isDateColumn(header)
                                        ? formatDateForInput(row[colIndex])
                                        : row[colIndex] || ""
                                    }
                                    onChange={(e) => {
                                      let value = e.target.value;
                                      // For numeric columns (Range LL, Range UL), only allow numbers and decimal point
                                      if (isNumericColumn(header)) {
                                        value = value.replace(/[^0-9.-]/g, "");
                                        // Allow only one decimal point
                                        const parts = value.split(".");
                                        if (parts.length > 2) {
                                          value =
                                            parts[0] +
                                            "." +
                                            parts.slice(1).join("");
                                        }
                                        // Allow only one minus sign at the beginning
                                        if (value.indexOf("-") > 0) {
                                          value = value.replace(/-/g, "");
                                        }
                                      }
                                      handleCellValueChange(
                                        rowIndex,
                                        colIndex,
                                        value,
                                      );
                                    }}
                                    type={
                                      isDateColumn(header)
                                        ? "date"
                                        : isNumericColumn(header)
                                          ? "text"
                                          : "text"
                                    }
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    disabled={isNonEditableColumn(header)}
                                    error={!!hasError}
                                    helperText={
                                      hasError
                                        ? errorMessage
                                        : isRequired
                                          ? alwaysRequired
                                            ? "Required *"
                                            : quantitativeRequired
                                              ? "Required for Quantitative *"
                                              : qualitativeRequired
                                                ? rangeType === "Calculative"
                                                  ? "Required for Calculative *"
                                                  : rangeType === "Others"
                                                    ? "Required for Others *"
                                                    : "Required for Qualitative *"
                                                : dropdownRequired
                                                  ? "Required *"
                                                  : "Required *"
                                          : isNumericColumn(header)
                                            ? "Numbers only"
                                            : ""
                                    }
                                    InputLabelProps={
                                      isDateColumn(header)
                                        ? { shrink: true }
                                        : undefined
                                    }
                                    sx={{
                                      "& .MuiInputBase-root": {
                                        fontSize: "0.875rem",
                                        minHeight: "auto",
                                      },
                                      "& .MuiInputBase-input": {
                                        padding: "6px 8px",
                                      },
                                      "& .MuiFormHelperText-root": {
                                        fontSize: "0.7rem",
                                        margin: "2px 0 0 0",
                                        lineHeight: 1.2,
                                      },
                                    }}
                                    InputProps={{
                                      readOnly: isNonEditableColumn(header),
                                      sx: {
                                        backgroundColor: isNonEditableColumn(
                                          header,
                                        )
                                          ? "#f5f5f5"
                                          : hasError
                                            ? "#ffebee"
                                            : isDateColumn(header)
                                              ? "#f0f8ff"
                                              : "white",
                                        "&:hover": {
                                          backgroundColor: isNonEditableColumn(
                                            header,
                                          )
                                            ? "#f5f5f5"
                                            : hasError
                                              ? "#ffebee"
                                              : isDateColumn(header)
                                                ? "#f0f8ff"
                                                : "#f9fafb",
                                        },
                                      },
                                    }}
                                  />
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No data to preview</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: "#f8fafc" }}>
          {hasUnsavedChanges && (
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "orange", fontWeight: "medium" }}
              >
                ⚠️ You have unsaved changes. Save your changes before uploading.
              </Typography>
            </Box>
          )}
          <Button
            onClick={handleClosePreviewModal}
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={Object.keys(validationErrors).length > 0}
          >
            {Object.keys(validationErrors).length > 0
              ? "Fix Errors First"
              : "Cancel"}
          </Button>
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a") && (
            <Button
              onClick={confirmBulkUpdate}
              variant="contained"
              disabled={
                loading ||
                Object.keys(validationErrors).length > 0 ||
                editedPreviewData.length === 0
              }
              startIcon={
                loading ? <CircularProgress size={20} /> : <CloudUploadIcon />
              }
              sx={{
                backgroundColor:
                  Object.keys(validationErrors).length > 0
                    ? "#9e9e9e"
                    : "#1976d2",
                "&:hover": {
                  backgroundColor:
                    Object.keys(validationErrors).length > 0
                      ? "#9e9e9e"
                      : "#1565c0",
                },
              }}
            >
              {loading
                ? "Uploading..."
                : Object.keys(validationErrors).length > 0
                  ? "Fix Validation Errors First"
                  : `Upload ${editedPreviewData.length} Records`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Binding Modal */}
      <Dialog
        open={bindingModalOpen}
        onClose={() => setBindingModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              🔗 Bind Equipment, Reference Material & Proficiency Testing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedExistingScopes.length} scope(s) selected for binding
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {/* Selected Scopes Summary */}
            <Card sx={{ p: 2, backgroundColor: "#e3f2fd" }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: "primary.main" }}
              >
                📋 Selected Scopes ({selectedExistingScopes.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {existingScopes
                  .filter((scope) => selectedExistingScopes.includes(scope.Id))
                  .map((scope, index) => (
                    <Chip
                      key={scope.Id}
                      label={`${index + 1}. ${scope.TestCode || "N/A"} - ${
                        scope.TypeOfTest
                      }`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
              </Box>
            </Card>

            {/* Binding Options */}
            <Grid container spacing={3}>
              {/* PT Programmes */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#9c27b0" }}>
                      🧪 PT Programmes
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search PT programmes..."
                      value={searchFilters.ptSearch}
                      onChange={(e) =>
                        handleSearchChange("ptSearch", e.target.value)
                      }
                      sx={{ mb: 2 }}
                      InputProps={{
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        },
                      }}
                    />
                    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                      {bindingLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : filteredPTProgrammes.length > 0 ? (
                        filteredPTProgrammes.map((pt) => (
                          <Box
                            key={pt.Id}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              mb: 2,
                              pb: 2,
                              borderBottom: "1px solid #e0e0e0",
                              "&:last-child": {
                                borderBottom: "none",
                                mb: 0,
                                pb: 0,
                              },
                            }}
                          >
                            <Checkbox
                              checked={bindingSelections.selectedPT.includes(
                                pt.Id,
                              )}
                              onChange={(e) =>
                                handleBindingSelectionChange(
                                  "selectedPT",
                                  pt.Id,
                                  e.target.checked,
                                )
                              }
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ ml: 1, flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1976d2",
                                  mb: 0.5,
                                }}
                              >
                                {pt.ProgramName ||
                                  pt.participationIn ||
                                  pt.Name ||
                                  `PT-${pt.Id}`}
                              </Typography>

                              {pt.nodalLaboratory &&
                                pt.nodalLaboratory !== "----Select----" &&
                                pt.nodalLaboratory.trim() !== "" && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "block",
                                      mt: 0.5,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <strong>Nodal Laboratory:</strong>{" "}
                                    {pt.nodalLaboratory}
                                  </Typography>
                                )}

                              {pt.ilcDetails && pt.ilcDetails.trim() !== "" && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 0.5,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  <strong>ILC Details:</strong> {pt.ilcDetails}
                                </Typography>
                              )}

                              {pt.remarks && pt.remarks.trim() !== "" && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 0.5,
                                    lineHeight: 1.4,
                                    fontStyle: "italic",
                                  }}
                                >
                                  <strong>Remarks:</strong> {pt.remarks}
                                </Typography>
                              )}

                              {pt.Provider && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#9c27b0",
                                    display: "inline-block",
                                    mt: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    bgcolor: "#f3e5f5",
                                    borderRadius: 1,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {pt.Provider}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          {searchFilters.ptSearch
                            ? `No PT programmes match "${searchFilters.ptSearch}"`
                            : "No PT programmes available"}
                        </Typography>
                      )}
                    </Box>
                    {filteredPTProgrammes.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {bindingSelections.selectedPT.length} of{" "}
                          {filteredPTProgrammes.length} shown (
                          {bindingData.ptProgrammes.length} total)
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Equipment */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#f57c00" }}>
                      ⚙️ Equipment
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search equipment..."
                      value={searchFilters.equipmentSearch}
                      onChange={(e) =>
                        handleSearchChange("equipmentSearch", e.target.value)
                      }
                      sx={{ mb: 2 }}
                      InputProps={{
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        },
                      }}
                    />
                    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                      {bindingLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : filteredEquipment.length > 0 ? (
                        filteredEquipment.map((equipment) => (
                          <Box
                            key={equipment.Id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Checkbox
                              checked={bindingSelections.selectedEquipment.includes(
                                equipment.Id,
                              )}
                              onChange={(e) =>
                                handleBindingSelectionChange(
                                  "selectedEquipment",
                                  equipment.Id,
                                  e.target.checked,
                                )
                              }
                              size="small"
                            />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {equipment.equipmentname ||
                                equipment.EquipmentName ||
                                equipment.Name ||
                                `Equipment-${equipment.Id}`}{" "}
                              ({equipment.Uid || "N/A"})
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          {searchFilters.equipmentSearch
                            ? `No equipment matches "${searchFilters.equipmentSearch}"`
                            : "No equipment available"}
                        </Typography>
                      )}
                    </Box>
                    {filteredEquipment.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {bindingSelections.selectedEquipment.length} of{" "}
                          {filteredEquipment.length} shown (
                          {bindingData.equipment.length} total)
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Reference Materials */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "#388e3c" }}>
                      📚 Reference Materials
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search reference materials..."
                      value={searchFilters.referenceMaterialSearch}
                      onChange={(e) =>
                        handleSearchChange(
                          "referenceMaterialSearch",
                          e.target.value,
                        )
                      }
                      sx={{ mb: 2 }}
                      InputProps={{
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        },
                      }}
                    />
                    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                      {bindingLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                          }}
                        >
                          <CircularProgress size={24} />
                        </Box>
                      ) : filteredReferenceMaterials.length > 0 ? (
                        filteredReferenceMaterials.map((material) => (
                          <Box
                            key={material.Id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Checkbox
                              checked={bindingSelections.selectedReferenceMaterial.includes(
                                material.Id,
                              )}
                              onChange={(e) =>
                                handleBindingSelectionChange(
                                  "selectedReferenceMaterial",
                                  material.Id,
                                  e.target.checked,
                                )
                              }
                              size="small"
                            />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {material.MaterialName ||
                                material.Name ||
                                `Material-${material.Id}`}{" "}
                              ({material.Traceability || "N/A"})
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          {searchFilters.referenceMaterialSearch
                            ? `No reference materials match "${searchFilters.referenceMaterialSearch}"`
                            : "No reference materials available"}
                        </Typography>
                      )}
                    </Box>
                    {filteredReferenceMaterials.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {bindingSelections.selectedReferenceMaterial.length}{" "}
                          of {filteredReferenceMaterials.length} shown (
                          {bindingData.referenceMaterials.length} total)
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Selection Summary */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Binding Summary:</strong>
                <br />• Scopes: {selectedExistingScopes.length} selected
                <br />• PT Programmes: {
                  bindingSelections.selectedPT.length
                }{" "}
                selected
                <br />• Equipment: {
                  bindingSelections.selectedEquipment.length
                }{" "}
                selected
                <br />• Reference Materials:{" "}
                {bindingSelections.selectedReferenceMaterial.length} selected
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setBindingModalOpen(false)}
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={bindingLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitBinding}
            variant="contained"
            disabled={
              bindingLoading ||
              (bindingSelections.selectedPT.length === 0 &&
                bindingSelections.selectedEquipment.length === 0 &&
                bindingSelections.selectedReferenceMaterial.length === 0)
            }
            startIcon={
              bindingLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
          >
            {bindingLoading ? "Binding..." : "Bind Selected Items"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Adequacy Check Modal */}
      <Dialog
        open={bulkAdequacyModalOpen}
        onClose={handleCloseBulkAdequacyModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          📋 Bulk Adequacy Check - {selectedAdequacyScopes.length} Scope(s)
          Selected
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          {/* Selected Scopes Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: "#28a745", fontWeight: 600 }}
            >
              📋 Selected Scopes ({selectedAdequacyScopes.length})
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(40,167,69,0.15)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(40,167,69,0.25)",
                    },
                  },
                }}
              >
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={bulkAdequacyStatus}
                  onChange={(e) => setBulkAdequacyStatus(e.target.value)}
                  label="Document Status"
                >
                  <MenuItem value="">-- Select Status --</MenuItem>
                  <MenuItem value="Adequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#28a745",
                        }}
                      />
                      Adequate
                    </Box>
                  </MenuItem>
                  <MenuItem value="Inadequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#dc3545",
                        }}
                      />
                      Inadequate
                    </Box>
                  </MenuItem>
                  {userTypeId === "4" && (
                    <MenuItem value="To be Verify">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#35c3dcff",
                          }}
                        />
                        To be Verify
                      </Box>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            {/* Clause Number dropdown for Assessment users when status is Inadequate */}
            {bulkAdequacyStatus === "Inadequate" && userTypeId === "6" && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clauseNo}
                  getOptionLabel={(option) =>
                    option?.DisplayName || option?.ClauseNo || option
                  }
                  value={selectedClause}
                  onChange={(event, newValue) => {
                    console.log(
                      "📍 ScopeSection - Bulk Clause selected:",
                      newValue,
                    );
                    setSelectedClause(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="📋 Select Clause Number"
                      variant="outlined"
                      size="medium"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option?.ClauseNo || option?.DisplayName || option}
                        </Typography>
                        {option?.DisplayName && option?.ClauseNo && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {option.DisplayName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  sx={{ mt: 1 }}
                />
              </Grid>
            )}
            {bulkAdequacyStatus === "Inadequate" && (
              <Grid item xs={12}>
                {/* Simple textarea for remarks */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Remarks (Required)"
                  placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                  value={bulkAdequacyRemarks || ""}
                  onChange={(e) =>
                    handleBulkAdequacyRemarksChange(e.target.value, false)
                  }
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: 2,
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(220,53,69,0.15)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 12px rgba(220,53,69,0.25)",
                      },
                    },
                  }}
                />

                {/* Predefined Remarks Dropdown */}
                {bulkScrutinyRemarks.length > 0 && (
                  <Box sx={{ mt: 2, position: "relative" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setIsBulkCheckboxDropdownOpen(
                          !isBulkCheckboxDropdownOpen,
                        )
                      }
                      sx={{
                        mb: 1,
                        borderColor: "#007bff",
                        color: "#007bff",
                        "&:hover": {
                          borderColor: "#0056b3",
                          backgroundColor: "#f8f9ff",
                        },
                      }}
                    >
                      {isBulkCheckboxDropdownOpen ? "Hide" : "Show"} Predefined
                      Remarks ({bulkScrutinyRemarks.length} available)
                    </Button>

                    {selectedBulkRemarksOptions.length > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearAllBulkCheckboxes}
                        sx={{ ml: 1, color: "#dc3545" }}
                      >
                        Clear All ({selectedBulkRemarksOptions.length})
                      </Button>
                    )}

                    {isBulkCheckboxDropdownOpen && (
                      <Paper
                        elevation={3}
                        sx={{
                          mt: 1,
                          maxHeight: 300,
                          overflow: "auto",
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                        }}
                      >
                        <MenuList dense>
                          {bulkScrutinyRemarks.map((option, index) => {
                            const remarkText =
                              typeof option === "string"
                                ? option
                                : option.inadequate_remarks;
                            const isSelected = selectedBulkRemarksOptions.some(
                              (item) =>
                                (typeof item === "string"
                                  ? item
                                  : item.inadequate_remarks) === remarkText,
                            );

                            return (
                              <MenuItem
                                key={index}
                                onClick={() =>
                                  handleBulkCheckboxChange(option, !isSelected)
                                }
                                sx={{
                                  py: 1,
                                  px: 2,
                                  display: "flex",
                                  alignItems: "flex-start",
                                  minHeight: "auto",
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{ p: 0, mr: 2 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.875rem",
                                      lineHeight: 1.4,
                                      wordBreak: "break-word",
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {remarkText}
                                  </Typography>
                                  {typeof option !== "string" &&
                                    option.type && (
                                      <Chip
                                        label={option.type}
                                        size="small"
                                        color={
                                          option.type === "Assessment"
                                            ? "primary"
                                            : "secondary"
                                        }
                                        variant="outlined"
                                        sx={{
                                          fontSize: "0.7rem",
                                          height: "18px",
                                          mt: 0.5,
                                        }}
                                      />
                                    )}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </Paper>
                    )}
                  </Box>
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: "#6c757d",
                    display: "block",
                    mt: 1,
                    fontStyle: "italic",
                  }}
                >
                  {bulkLoadingRemarks
                    ? "Loading existing remarks..."
                    : bulkScrutinyRemarks.length > 0
                      ? `${bulkScrutinyRemarks.length} predefined remark(s) available for "Scope". You can select multiple options from the dropdown above.`
                      : "Type your detailed remarks in the text area above."}
                </Typography>
              </Grid>
            )}
          </Grid>

          {bulkAdequacyStatus && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity={
                  bulkAdequacyStatus === "Adequate" ? "success" : "warning"
                }
                sx={{ borderRadius: 2 }}
              >
                {bulkAdequacyStatus === "Adequate"
                  ? `✓ ${selectedAdequacyScopes.length} scope(s) will be marked as Adequate.`
                  : `⚠ ${selectedAdequacyScopes.length} scope(s) will be marked as Inadequate. Please ensure remarks are provided.`}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseBulkAdequacyModal}
            sx={{ color: "#6c757d" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkAdequacySubmit}
            disabled={
              bulkAdequacyLoading ||
              !bulkAdequacyStatus ||
              (bulkAdequacyStatus === "Inadequate" &&
                !bulkAdequacyRemarks?.trim())
            }
            sx={{
              background:
                bulkAdequacyStatus === "Adequate"
                  ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                  : "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
              color: "white",
              fontWeight: 600,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              },
            }}
          >
            {bulkAdequacyLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "white" }} />
                Submitting...
              </Box>
            ) : (
              `📤 Mark ${selectedAdequacyScopes.length} Scope(s) as ${
                bulkAdequacyStatus || "Selected Status"
              }`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adequacy Check Popup */}
      <AdequacyCheckPopup
        open={adequacyModalOpen}
        onClose={handleCloseAdequacyModal}
        scopeId={currentAdequacyScopeId}
        scopeTitle={currentAdequacyScopeTitle}
        adequacyCheck={adequacyChecks[currentAdequacyScopeId]}
        onAdequacyChange={handleAdequacyChange}
        onRemarksChange={handleAdequacyRemarks}
        onSubmit={handleAdequacySubmit}
        loading={adequacyLoading}
        // Clause-related props for Assessment users
        clauseNo={clauseNo}
        selectedClause={selectedClause}
        onClauseChange={setSelectedClause}
        userTypeId={getUserTypeId()}
      />

      {/* Performance Score Dialog */}
      <Dialog
        open={performanceScoreDialogOpen}
        onClose={handleClosePerformanceScoreDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Enter Performance Score
          {selectedPtForScore && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Scope:</strong> {selectedPtForScore.scopeName}
              <br />
              <strong>PT Programme:</strong> {selectedPtForScore.ptName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Lab Code"
              value={performanceScoreForm.labCode}
              onChange={(e) =>
                handlePerformanceScoreChange("labCode", e.target.value)
              }
              fullWidth
              required
              disabled={performanceScoreLoading}
            />
            <TextField
              label="Performance Score"
              value={performanceScoreForm.performanceScore}
              onChange={(e) =>
                handlePerformanceScoreChange("performanceScore", e.target.value)
              }
              fullWidth
              required
              disabled={performanceScoreLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePerformanceScoreDialog}
            disabled={performanceScoreLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePerformanceScore}
            variant="contained"
            color="primary"
            disabled={
              performanceScoreLoading ||
              !performanceScoreForm.labCode ||
              !performanceScoreForm.performanceScore
            }
            startIcon={
              performanceScoreLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {performanceScoreForm.editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Edit Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalScopeSection;
